import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard, Plus, Trash2, LogOut, Film, Link2, ExternalLink,
  Save, Eye, EyeOff, Download, Upload, Star, Calendar, Clock, Loader2, Copy, Check,
  Users, MessageSquare, UserCog, Megaphone, Activity, BookOpen, Send, FileText, Mail, Ban, Play,
  ArrowUp, ArrowDown, AlertTriangle, GitCompare, RadioTower,
} from "lucide-react";
import { toast } from "sonner";
import { usePendingCount } from "@/lib/pending-publisher";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useAuth } from "@/lib/auth";
import { useAdConfig, DEFAULT_AD_CONFIG } from "@/lib/ad-config";
import { useSiteAds, DEFAULT_SITE_ADS, ALL_SLOT_KEYS, SITE_ADS_REMOTE_REPO_PATH, type AdSlotKey, type SlotConfig } from "@/lib/site-ads";
import { uploadTextToGithub } from "@/lib/github-publisher";
import { LogsTab } from "@/components/rawan/LogsTab";


import {
  addItem, deleteItem, useAllItems, exportLibraryJSON, importLibraryJSON, useCustomItems,
  type CustomMovie,
} from "@/lib/library-store";
import type { DownloadLink } from "@/lib/movies-data";
import { localizeEpisodeTitle } from "@/lib/episode-title";
import { ArticleEditor } from "@/components/rawan/ArticleEditor";
import { ArticleRenderer } from "@/components/rawan/ArticleRenderer";
import type { Article } from "@/lib/article-types";

import { addEpisode, deleteEpisode, useEpisodes } from "@/lib/episodes-store";
import { getAllMovies, getAllSeries, getSeasonsForSeries as getSeasonsRaw, getRawSeasonForSeries } from "@/lib/content/loader";
import type { ContentSeason } from "@/lib/content/schema";
import { useSocialLinks, setSocialLinks, SOCIAL_REMOTE_PATH, DEFAULT_SOCIAL_LINKS, type SocialKey } from "@/lib/social-links";
import { Share2 } from "lucide-react";
import {
  useEpisodeOverrides,
  getMergedSeasonsForSeries,
  cleanEpisodeOverride,
  setOverride,
  clearOverride,
} from "@/lib/episode-overrides";
import { getGitHubConfig, saveGitHubConfig, clearGitHubConfig, uploadJsonToGithub, type GitHubConfig } from "@/lib/github-publisher";
import {
  archiveCommentsToGitHub,
  downloadArchiveBundle,
  fetchAllComments,
  groupAndSummarize,
  purgeArchivedCommentsOlderThan,
  type ArchiveResult,
} from "@/lib/comments-archive";
import { Archive, MessageCircle, Sparkles, CalendarClock, Bell } from "lucide-react";
import { StoriesManager } from "@/components/rawan/StoriesManager";
import { UpcomingEpisodePanel } from "@/components/rawan/UpcomingEpisodePanel";
import { FcmConfigPanel, isFcmPanelUnlocked } from "@/components/rawan/FcmConfigPanel";
import { NotifyConfigPanel } from "@/components/rawan/NotifyConfigPanel";
import { PendingPanel } from "@/components/rawan/PendingPanel";
import { R2ManagerPanel } from "@/components/rawan/R2ManagerPanel";
import { R2SettingsPanel } from "@/components/rawan/R2SettingsPanel";
import { R2StatusPanel } from "@/components/rawan/R2StatusPanel";
import { Cloud } from "lucide-react";
import { DownloadLinksEditor } from "@/components/rawan/DownloadLinksEditor";

export const Route = createFileRoute("/dashboard/")({
  head: () => ({
    meta: [
      { title: "لوحة التحكم — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DashboardPage,
});

type TabKey = "content" | "episodes" | "ads" | "siteads" | "social" | "logs" | "publish" | "data" | "comments" | "upcoming" | "fcm" | "notify" | "pending" | "stories" | "r2" | "r2cfg" | "r2status";

function DashboardPage() {
  const { isAuthed } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthed) navigate({ to: "/login" });
  }, [isAuthed, navigate]);

  // بوابة مصادقة رفيعة: نُبقي كل الـ hooks الثقيلة داخل DashboardInner الذي لا
  // يُركَّب إلا بعد التحقق من الدخول، فيبقى عدد الـ hooks ثابتاً ولا يحدث خطأ.
  if (!isAuthed) return null;
  return <DashboardInner />;
}

const DASHBOARD_TAB_STORAGE_KEY = "rawan-dashboard-active-tab-v1";
const VALID_TAB_KEYS: TabKey[] = [
  "content","episodes","ads","siteads","social","logs","publish",
  "data","comments","upcoming","fcm","notify","pending","stories","r2", "r2cfg", "r2status"];

function DashboardInner() {
  const { email, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>(() => {
    if (typeof window === "undefined") return "content";
    try {
      const saved = window.localStorage.getItem(DASHBOARD_TAB_STORAGE_KEY);
      if (saved && (VALID_TAB_KEYS as string[]).includes(saved)) return saved as TabKey;
    } catch { /* ignore */ }
    return "content";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { window.localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, tab); } catch { /* ignore */ }
  }, [tab]);
  const pendingCount = usePendingCount();

  // تصغير افتراضي 75% على الهاتف فقط — يجعل لوحة التحكم مريحة دون تكسير التصميم.
  const [zoomMobile, setZoomMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setZoomMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const fcmUnlocked = isFcmPanelUnlocked();

  const tabs: { key: TabKey; label: string; icon: typeof Film }[] = [
    { key: "pending",  label: "المسودّات",  icon: Send },
    { key: "content",  label: "المحتوى",   icon: Film },   
    { key: "episodes", label: "الحلقات",    icon: LayoutDashboard },
    { key: "upcoming", label: "الحلقة القادمة", icon: CalendarClock },
    { key: "stories",  label: "إدارة القصص", icon: Sparkles },
    { key: "ads",      label: "إعلانات المشغّل",   icon: Megaphone },
    { key: "siteads",  label: "إعلانات الموقع",   icon: Megaphone },
    { key: "social",   label: "روابط السوشيال", icon: Share2 },
    { key: "logs",     label: "سجل الوصول",  icon: Activity },
    { key: "publish",  label: "إعدادات GitHub", icon: Upload },
    { key: "comments", label: "أرشفة التعليقات",      icon: Archive },
    { key: "data",     label: "النسخ الاحتياطي", icon: Download },
    ...(fcmUnlocked ? [{ key: "fcm" as TabKey, label: "إشعارات FCM", icon: Bell }] : []),
    { key: "notify",   label: "إشعارات (Worker)", icon: Bell },
    { key: "r2",       label: "تخزين R2", icon: Cloud },
    { key: "r2cfg",    label: "إعدادات R2", icon: Cloud },
    { key: "r2status", label: "حالة R2", icon: Cloud },
  ];


  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main
        className="flex-1 container mx-auto px-3 sm:px-4 py-6 sm:py-8"
        style={zoomMobile ? ({ zoom: 0.75 } as Record<string, string | number>) : undefined}
      >
        <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-11 w-11 sm:h-12 sm:w-12 shrink-0 place-items-center rounded-2xl" style={{ background: "var(--gradient-primary)" }}>
              <LayoutDashboard className="h-5 w-5 sm:h-6 sm:w-6 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg sm:text-2xl font-extrabold">لوحة التحكم</h1>
              <p className="truncate text-[11px] sm:text-xs text-muted-foreground">مرحباً {email}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 lg:shrink-0 lg:justify-end">
            <Link to="/account" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <UserCog className="h-4 w-4" /> <span className="hidden xl:inline">حساب الأدمن</span>
            </Link>
            <Link to="/admin/users" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <Users className="h-4 w-4" /> <span className="hidden xl:inline">المستخدمين</span>
            </Link>
            <Link to="/admin/comments" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <MessageSquare className="h-4 w-4" /> <span className="hidden xl:inline">التعليقات</span>
            </Link>
            <Link to="/admin/safelink" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <Link2 className="h-4 w-4" /> <span className="hidden xl:inline">رابط محمي</span>
            </Link>
            <Link to="/admin/preview" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-primary/40 hover:bg-primary/10 text-xs sm:text-sm">
              <Eye className="h-4 w-4 text-primary" /> <span className="hidden xl:inline">معاينة</span>
            </Link>            
            <Link to="/admin/contact" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <Mail className="h-4 w-4" /> <span className="hidden xl:inline">اتصل بنا</span>
            </Link>
            <Link to="/admin/newsletter" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <Send className="h-4 w-4" /> <span className="hidden xl:inline">النشرة البريدية</span>
            </Link>
            <Link to="/admin/blocked-countries" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-destructive/40 hover:bg-destructive/10 text-xs sm:text-sm">
              <Ban className="h-4 w-4 text-destructive" /> <span className="hidden xl:inline">حظر الدول</span>
              </Link>
              <Link to="/admin/monitor" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-primary/40 hover:bg-primary/10 text-xs sm:text-sm">
                <RadioTower className="h-4 w-4 text-primary" /> <span className="hidden xl:inline">الرادار</span>
              </Link>
              <Link to="/admin/help" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs sm:text-sm">
              <BookOpen className="h-4 w-4" /> <span className="hidden xl:inline">الدليل</span>
            </Link>
            <button onClick={() => { logout(); navigate({ to: "/" }); }} className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground text-xs sm:text-sm">
              <LogOut className="h-4 w-4" /> <span className="hidden xl:inline">خروج</span>
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="mb-5 flex gap-1 p-1 bg-card border border-border rounded-xl overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold whitespace-nowrap transition ${
                  active ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
                {t.key === "pending" && pendingCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"}`}>
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "content" && <ContentTab />}
        {tab === "episodes" && <EpisodesTab />}
        {tab === "ads" && <AdsTab />}
        {tab === "siteads" && <SiteAdsTab />}
        {tab === "logs" && <LogsTab />}
        {tab === "social" && <SocialTab />}
        {tab === "publish" && <PublishTab />}
        {tab === "comments" && <CommentsArchiveTab />}
        {tab === "data" && <DataTab />}
        {tab === "upcoming" && <UpcomingEpisodePanel />}
        {tab === "fcm" && fcmUnlocked && <FcmConfigPanel />}
        {tab === "notify" && <NotifyConfigPanel />}
        {tab === "pending" && <PendingPanel />}
        {tab === "stories" && <StoriesManager />}
        {tab === "r2" && <R2ManagerPanel />}
        {tab === "r2cfg" && <R2SettingsPanel />}
        {tab === "r2status" && <R2StatusPanel />}
      </main>
      <Footer />
    </div>
  );
}


/* =================== Content tab =================== */

const emptyDraft = (): CustomMovie => ({
  id: crypto.randomUUID(),
  slug: "",
  title: "", titleEn: "", titleFr: "",
  type: "movie",
  year: new Date().getFullYear(),
  rating: 8.0,
  duration: "", durationEn: "", durationFr: "",
  seasonInfo: "", seasonInfoEn: "", seasonInfoFr: "",
  status: "", statusEn: "", statusFr: "",
  genres: [], genresEn: [], genresFr: [],
  poster: "", backdrop: "",
  overview: "", overviewEn: "", overviewFr: "",
  cast: [], director: "",
  trailer: "",
  downloads: [],
  article: [],
  isNew: true,
});

function slugify(s: string) {
  return s.toLowerCase().trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9\u0600-\u06FF]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function ContentTab() {
  const allItems = useAllItems();
  const customItems = useCustomItems();
  const [draft, setDraft] = useState<CustomMovie>(emptyDraft);
  const [genresText, setGenresText] = useState("");
  const [genresEnText, setGenresEnText] = useState("");
  const [genresFrText, setGenresFrText] = useState("");
  const [castText, setCastText] = useState("");
  const [preview, setPreview] = useState(false);
  const [saved, setSaved] = useState(false);
  /** When non-empty, indicates the form is editing an existing item by slug.
   *  Save still goes through addItem(), which dedupes by slug and replaces. */
  const [editingSlug, setEditingSlug] = useState<string>("");

  function update<K extends keyof CustomMovie>(key: K, value: CustomMovie[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function setArticle(next: Article) {
    setDraft((d) => ({ ...d, article: next }));
  }

  const slug = draft.slug || slugify(draft.titleEn || draft.title);

  function reset() {
    setDraft(emptyDraft());
    setGenresText(""); setGenresEnText(""); setGenresFrText(""); setCastText("");
    setSaved(false); setPreview(false);
    setEditingSlug("");
  }

  /** Load an existing item (from custom store, JSON content, or built-in seed)
   *  into the form so the admin can update its data + article in place. */
  function loadForEdit(targetSlug: string) {
    if (!targetSlug) { reset(); return; }
    const item = allItems.find((m) => m.slug === targetSlug);
    if (!item) return;
    setDraft({
      id: item.id || crypto.randomUUID(),
      slug: item.slug,
      title: item.title ?? "",
      titleEn: item.titleEn ?? "",
      titleFr: item.titleFr ?? "",
      type: item.type,
      year: item.year ?? new Date().getFullYear(),
      rating: item.rating ?? 0,
      duration: item.duration ?? "",
      durationEn: (item as { durationEn?: string }).durationEn ?? "",
      durationFr: (item as { durationFr?: string }).durationFr ?? "",
      seasonInfo: (item as { seasonInfo?: string }).seasonInfo ?? "",
      seasonInfoEn: (item as { seasonInfoEn?: string }).seasonInfoEn ?? "",
      seasonInfoFr: (item as { seasonInfoFr?: string }).seasonInfoFr ?? "",
      status: (item as { status?: string }).status ?? "",
      statusEn: (item as { statusEn?: string }).statusEn ?? "",
      statusFr: (item as { statusFr?: string }).statusFr ?? "",
      genres: item.genres ?? [],
      genresEn: item.genresEn ?? [],
      genresFr: item.genresFr ?? [],
      poster: item.poster ?? "",
      backdrop: item.backdrop ?? "",
      overview: item.overview ?? "",
      overviewEn: item.overviewEn ?? "",
      overviewFr: item.overviewFr ?? "",
      cast: item.cast ?? [],
      director: item.director ?? "",
      trailer: item.trailer ?? "",
      // downloads[] is hydrated by the adapter (it already surfaces watch_url
      // as a "watch" row), so we render it as-is — admins see and can edit
      // every previously-published link.
      downloads: (item.downloads ?? []).slice(),
      article: (item as CustomMovie).article ?? [],
      isNew: !!item.isNew,
      isTrending: !!(item as { isTrending?: boolean }).isTrending,
      country: item.country,
      releaseDate: item.releaseDate,
      firstAirDate: (item as { firstAirDate?: string }).firstAirDate,
      lastAirDate: (item as { lastAirDate?: string }).lastAirDate,
      watchToken: (item as { watchToken?: string }).watchToken,
      tags: (item as { tags?: string[] }).tags ?? [],
      seasonsCount: (item as { seasonsCount?: number }).seasonsCount,
      durationMinutes: (item as { durationMinutes?: number }).durationMinutes,
      videoSource: (item as { videoSource?: CustomMovie["videoSource"] }).videoSource,
      // Round-trip: carry the raw source JSON so `toMovieJson()` can
      // preserve every field (even ones the dashboard doesn't render).
      __raw: (item as { __raw?: Record<string, unknown> }).__raw,
    } as CustomMovie);
    setGenresText((item.genres ?? []).join("، "));
    setGenresEnText((item.genresEn ?? []).join(", "));
    setGenresFrText((item.genresFr ?? []).join(", "));
    setCastText((item.cast ?? []).join("، "));
    setEditingSlug(targetSlug);
    setSaved(false);
  }

  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState<string | null>(null);

  function buildFinalItem(): CustomMovie {
    const finalSlug = (editingSlug || slug || slugify(draft.title) || `item-${Date.now()}`);
    return {
      ...draft,
      slug: finalSlug,
      genres: genresText.split(/[,،]/).map((s) => s.trim()).filter(Boolean),
      genresEn: genresEnText.split(",").map((s) => s.trim()).filter(Boolean),
      genresFr: genresFrText.split(",").map((s) => s.trim()).filter(Boolean),
      cast: castText.split(/[,،]/).map((s) => s.trim()).filter(Boolean),
      article: draft.article ?? [],
      paragraphs: undefined,
    } as CustomMovie;
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.title.trim() || !draft.titleEn.trim()) return;
    const item = buildFinalItem();
    addItem(item);
    setSaved(true);
    setEditingSlug(item.slug);
    setTimeout(() => setSaved(false), 2200);
  }

  /** Save locally AND publish the JSON to the configured GitHub repo in one click. */
  async function handleSaveAndPublish() {
    if (!draft.title.trim() || !draft.titleEn.trim()) {
      setPublishMsg("أضف العنوان بالعربية والإنجليزية أولاً.");
      setTimeout(() => setPublishMsg(null), 2400);
      return;
    }
    const item = buildFinalItem();
    addItem(item);
    setEditingSlug(item.slug);
    setSaved(true);
    setPublishing(true);
    setPublishMsg("جارٍ حفظ المسودّة…");
    const { json, relPath } = toMovieJson(item);
    const res = await uploadJsonToGithub({
      relativePath: relPath,
      jsonContent: json,
      message: `chore(content): publish ${item.title} (${item.slug})`,
    });
    setPublishing(false);
    if (res.ok) {
      setPublishMsg(`تمت إضافته إلى المسودّات ✓ — افتح تبويب «المسودّات» ثم اضغط «نشر الكل» للرفع إلى GitHub.`);
    } else {
      setPublishMsg(`فشل حفظ المسودّة: ${res.error}`);
    }
    setTimeout(() => { setSaved(false); }, 2200);
    setTimeout(() => setPublishMsg(null), 6500);
  }

  return (
    <div className="grid xl:grid-cols-[1.1fr_1fr] gap-5">
      {/* Form */}
      <form onSubmit={handleSave} className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            <h2 className="text-base sm:text-lg font-extrabold">
              {editingSlug ? "تحديث محتوى موجود" : "إضافة فيلم أو مسلسل جديد"}
            </h2>
          </div>
          <button type="button" onClick={() => setPreview((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-accent">
            <Eye className="h-3.5 w-3.5" /> {preview ? "إخفاء المعاينة" : "معاينة"}
          </button>
        </div>

        {/* Existing-item picker — loads a movie/series into the form for editing. */}
        <div className="mb-4 p-3 rounded-xl bg-background border border-dashed border-border">
          <Label>تعديل عنصر منشور (فيلم أو مسلسل)</Label>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            <select
              value={editingSlug}
              onChange={(e) => loadForEdit(e.target.value)}
              className="flex-1 min-w-[200px] px-3 py-2 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— اختر عنصراً موجوداً للتحديث —</option>
              <optgroup label="أفلام">
                {allItems.filter((m) => m.type === "movie").map((m) => (
                  <option key={`m-${m.slug}`} value={m.slug}>{m.title} — {m.titleEn} ({m.year})</option>
                ))}
              </optgroup>
              <optgroup label="مسلسلات">
                {allItems.filter((m) => m.type === "series").map((m) => (
                  <option key={`s-${m.slug}`} value={m.slug}>{m.title} — {m.titleEn} ({m.year})</option>
                ))}
              </optgroup>
            </select>
            {editingSlug && (
              <button type="button" onClick={reset}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-accent text-xs">
                <Plus className="h-3.5 w-3.5" /> عنصر جديد
              </button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            عند الحفظ يُحدَّث العنصر بنفس المعرف (slug) ويُخزَّن في متصفحك. لرفعه فعلياً للزوار اضغط «حفظ في المسودّات» ثم انشر دفعة واحدة من تبويب «المسودّات».
          </p>
        </div>



        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>النوع</Label>
            <div className="mt-1.5 flex gap-2">
              {(["movie", "series"] as const).map((typ) => (
                <button type="button" key={typ}
                  onClick={() => update("type", typ)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border ${
                    draft.type === typ ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-accent"
                  }`}>
                  {typ === "movie" ? "فيلم" : "مسلسل"}
                </button>
              ))}
            </div>
          </div>

          <Field label="العنوان بالعربية" value={draft.title} onChange={(v) => update("title", v)} required />
          <Field label="العنوان بالإنجليزية" value={draft.titleEn} onChange={(v) => update("titleEn", v)} required />
          <div className="col-span-2">
            <Field label="Titre en français (اختياري)" value={draft.titleFr ?? ""} onChange={(v) => update("titleFr", v)} placeholder="Titre français…" />
          </div>

          <Field label="المعرف (slug)" value={draft.slug} onChange={(v) => update("slug", v)} placeholder={slug || "auto-generated"} />
          <Field label="السنة" type="number" value={String(draft.year)} onChange={(v) => update("year", parseInt(v || "0", 10))} />

          <Field label="التقييم (0-10)" type="number" value={String(draft.rating)} onChange={(v) => update("rating", parseFloat(v || "0"))} />
          <Field label="المدة (عربي)" value={draft.duration} onChange={(v) => update("duration", v)} placeholder="2س 30د" />
          <Field label="Duration (English)" value={draft.durationEn ?? ""} onChange={(v) => update("durationEn", v)} placeholder="2h 30m" />
          <Field label="Durée (Français)" value={draft.durationFr ?? ""} onChange={(v) => update("durationFr", v)} placeholder="2h 30m" />
          {draft.type === "series" && (
            <>
              <Field label="معلومات المواسم (عربي)" value={draft.seasonInfo ?? ""} onChange={(v) => update("seasonInfo", v)} placeholder="3 مواسم" />
              <Field label="Seasons (English)" value={draft.seasonInfoEn ?? ""} onChange={(v) => update("seasonInfoEn", v)} placeholder="3 seasons" />
              <Field label="Saisons (Français)" value={draft.seasonInfoFr ?? ""} onChange={(v) => update("seasonInfoFr", v)} placeholder="3 saisons" />
              <Field label="عدد المواسم (رقم)" type="number" value={String(draft.seasonsCount ?? "")} onChange={(v) => update("seasonsCount", parseInt(v || "0", 10) || undefined)} placeholder="3" />
              <Field label="حالة المسلسل (عربي)" value={draft.status ?? ""} onChange={(v) => update("status", v)} placeholder="مستمر / مكتمل" />
              <Field label="Status (English)" value={draft.statusEn ?? ""} onChange={(v) => update("statusEn", v)} placeholder="Ongoing / Completed" />
              <Field label="Statut (Français)" value={draft.statusFr ?? ""} onChange={(v) => update("statusFr", v)} placeholder="En cours / Terminée" />
            </>
          )}

          <Field label="المخرج" value={draft.director} onChange={(v) => update("director", v)} />
          <Field label="التريلر (YouTube)" value={draft.trailer} onChange={(v) => update("trailer", v)} placeholder="https://youtube.com/…" />

          <Field label="بوستر (رابط صورة)" value={draft.poster} onChange={(v) => update("poster", v)} placeholder="https://…/poster.jpg" />
          <Field label="خلفية كبيرة (رابط صورة)" value={draft.backdrop} onChange={(v) => update("backdrop", v)} placeholder="https://…/backdrop.jpg" />

          <Field label="الدولة (ISO code — US, FR, EG…)" value={draft.country ?? ""} onChange={(v) => update("country", v as CustomMovie["country"])} placeholder="US" />
          <Field label="المدة بالدقائق (رقم اختياري)" type="number" value={draft.durationMinutes ? String(draft.durationMinutes) : ""} onChange={(v) => update("durationMinutes", v ? parseInt(v, 10) : undefined)} placeholder="120" />

          {draft.type === "movie" ? (
            <div className="col-span-2">
              <Field label="تاريخ الإصدار (release_date — YYYY-MM-DD)" value={draft.releaseDate ?? ""} onChange={(v) => update("releaseDate", v)} placeholder="2024-06-15" />
            </div>
          ) : (
            <>
              <Field label="تاريخ أول بث (first_air_date)" value={draft.firstAirDate ?? ""} onChange={(v) => update("firstAirDate", v)} placeholder="2017-12-01" />
              <Field label="تاريخ آخر بث (last_air_date)" value={draft.lastAirDate ?? ""} onChange={(v) => update("lastAirDate", v)} placeholder="2020-06-27" />
            </>
          )}

          <Field label="التصنيفات بالعربية (مفصولة بفاصلة)" value={genresText} onChange={setGenresText} placeholder="أكشن، دراما" />
          <Field label="التصنيفات بالإنجليزية" value={genresEnText} onChange={setGenresEnText} placeholder="Action, Drama" />
          <div className="col-span-2">
            <Field label="Genres en français (اختياري)" value={genresFrText} onChange={setGenresFrText} placeholder="Action, Drame" />
          </div>

          <div className="col-span-2">
            <Field label="طاقم العمل (مفصول بفاصلة)" value={castText} onChange={setCastText} placeholder="ممثل 1، ممثل 2…" />
          </div>

          <div className="col-span-2">
            <Label>ملخص قصير (عربي)</Label>
            <textarea
              value={draft.overview}
              onChange={(e) => update("overview", e.target.value)}
              rows={2}
              className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="col-span-2">
            <Label>Short overview (English)</Label>
            <textarea
              value={draft.overviewEn}
              onChange={(e) => update("overviewEn", e.target.value)}
              rows={2}
              className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="col-span-2">
            <Label>Résumé court (Français)</Label>
            <textarea
              value={draft.overviewFr ?? ""}
              onChange={(e) => update("overviewFr", e.target.value)}
              rows={2}
              className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>


          <div className="col-span-2">
            <Label>المقال (يظهر قبل صندوق التعليقات)</Label>
            <p className="text-[11px] text-muted-foreground mt-1 mb-2">
              أضف فقرات وعناوين وقوائم وتنبيهات وجداول وصور. كل كتلة قابلة للترتيب والحذف، وبعضها يدعم الطي والحرف الأول الكبير.
            </p>
            <ArticleEditor value={draft.article ?? []} onChange={setArticle} />
          </div>

          <div className="col-span-2">
            <Label>روابط المشاهدة والتحميل</Label>
            <p className="text-[11px] text-muted-foreground mt-1">
              لكل رابط: حدد نوعه (مشاهدة مباشرة عبر الإنترنت أو تحميل) وجودته وحجمه واسم السيرفر والرابط. هذه الروابط دائمًا متاحة للتعديل لأنها قد تتغير باستمرار.
            </p>
            <div className="grid gap-2 mt-2">
              {(draft.downloads ?? []).map((d, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                  <select
                    value={d.kind ?? "download"}
                    onChange={(e) => {
                      const next = [...(draft.downloads ?? [])];
                      next[idx] = { ...d, kind: e.target.value as DownloadLink["kind"] };
                      update("downloads", next);
                    }}
                    className="col-span-2 px-2 py-2 bg-background text-foreground border border-border rounded-lg text-xs"
                    title="نوع الرابط"
                  >
                    <option value="download">تحميل</option>
                    <option value="watch">مشاهدة</option>
                  </select>
                  <select
                    value={d.quality}
                    onChange={(e) => {
                      const next = [...(draft.downloads ?? [])];
                      next[idx] = { ...d, quality: e.target.value as DownloadLink["quality"] };
                      update("downloads", next);
                    }}
                    className="col-span-2 px-2 py-2 bg-background text-foreground border border-border rounded-lg text-xs"
                  >
                    <option value="480p">480p</option>
                    <option value="720p">720p</option>
                    <option value="1080p">1080p</option>
                    <option value="4K">4K</option>
                  </select>
                  <input
                    type="text"
                    value={d.size}
                    onChange={(e) => {
                      const next = [...(draft.downloads ?? [])];
                      next[idx] = { ...d, size: e.target.value };
                      update("downloads", next);
                    }}
                    placeholder="الحجم"
                    className="col-span-1 px-2 py-2 bg-background text-foreground border border-border rounded-lg text-xs"
                  />
                  <input
                    type="text"
                    value={d.server}
                    onChange={(e) => {
                      const next = [...(draft.downloads ?? [])];
                      next[idx] = { ...d, server: e.target.value };
                      update("downloads", next);
                    }}
                    placeholder="السيرفر"
                    className="col-span-2 px-2 py-2 bg-background text-foreground border border-border rounded-lg text-xs"
                  />
                  <input
                    type="url"
                    value={d.url}
                    onChange={(e) => {
                      const next = [...(draft.downloads ?? [])];
                      next[idx] = { ...d, url: e.target.value };
                      update("downloads", next);
                    }}
                    placeholder="رابط المشاهدة أو التحميل"
                    className="col-span-4 px-2 py-2 bg-background text-foreground border border-border rounded-lg text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = (draft.downloads ?? []).filter((_, i) => i !== idx);
                      update("downloads", next);
                    }}
                    className="col-span-1 p-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground text-xs"
                  >
                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const next = [...(draft.downloads ?? []), { kind: "download" as const, quality: "1080p" as DownloadLink["quality"], size: "", server: "Server 1", url: "" }];
                    update("downloads", next);
                  }}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border hover:bg-accent text-xs"
                >
                  <Plus className="h-3.5 w-3.5" /> رابط تحميل
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = [...(draft.downloads ?? []), { kind: "watch" as const, quality: "1080p" as DownloadLink["quality"], size: "", server: "Server 1", url: "" }];
                    update("downloads", next);
                  }}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-border hover:bg-accent text-xs"
                >
                  <Plus className="h-3.5 w-3.5" /> رابط مشاهدة
                </button>
              </div>
            </div>
          </div>

          <div className="col-span-2 flex flex-wrap gap-3 pt-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.isNew} onChange={(e) => update("isNew", e.target.checked)} className="accent-[var(--primary)]" />
              جديد
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.isTrending} onChange={(e) => update("isTrending", e.target.checked)} className="accent-[var(--primary)]" />
              رائج
            </label>
          </div>
        </div>


        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* زر 1 — حفظ محلي في المتصفح فقط (Storage) */}
          <button type="submit" className="btn-primary inline-flex items-center justify-center gap-2 py-2.5">
            <Save className="h-4 w-4" />
            {saved ? "تم الحفظ ✓" : (editingSlug ? "حفظ التحديث في المتصفح" : "حفظ في المتصفح")}
          </button>

          {/* زر 2 — حفظ في المسودّات (يحفظ محلياً ثم يضيف إلى قائمة المسودّات للنشر دفعة واحدة) */}
          <button
            type="button"
            onClick={handleSaveAndPublish}
            disabled={publishing}
            className="inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-600/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-600/25 text-sm disabled:opacity-50"
            title="أضف هذا التعديل إلى قائمة المسودّات. لن يُرفع إلى GitHub حتى تضغط «نشر الكل» من تبويب المسودّات."
          >
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            حفظ في المسودّات
          </button>
        </div>
        <div className="mt-2 flex justify-end">
          <button type="button" onClick={reset} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:bg-accent text-xs">
            مسح النموذج
          </button>
        </div>
        {publishMsg && (
          <div className="mt-3 text-xs whitespace-pre-line bg-primary/10 border border-primary/30 rounded-lg p-2.5 text-foreground/90 break-all">
            {publishMsg}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">
          <b>زر «حفظ في المتصفح»</b>: يخزّن نسخة محلية للمعاينة الفورية في هذا المتصفح فقط — لا يلامس المسودّات ولا GitHub.
          <br />
          <b>زر «حفظ في المسودّات»</b>: يضيف الملف إلى قائمة المسودّات. يتجمّع مع باقي التعديلات في تبويب «المسودّات»، ثم تنشرها كلها معًا إلى GitHub بضغطة واحدة.
        </p>
      </form>

      {/* Side: preview + list */}
      <div className="space-y-5">
        {preview && <PreviewCard item={{ ...draft, slug: slug || "preview", genres: genresText.split(",").map((s) => s.trim()).filter(Boolean), genresEn: genresEnText.split(",").map((s) => s.trim()).filter(Boolean), genresFr: genresFrText.split(",").map((s) => s.trim()).filter(Boolean) }} />}

        <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-extrabold inline-flex items-center gap-2">
              <Film className="h-5 w-5 text-primary" /> العناصر المنشورة ({customItems.length})
            </h2>
            <span className="text-[11px] text-muted-foreground">المكتبة الكلية: {allItems.length}</span>
          </div>
          {customItems.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-xl">
              لم تنشر أي محتوى مخصص بعد.
            </div>
          ) : (
            <ul className="grid gap-2">
              {customItems.map((m) => (
                <li key={m.slug} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-3 rounded-xl bg-background border border-border">
                  <div className="w-12 h-16 rounded-md overflow-hidden bg-muted shrink-0">
                    {m.poster && <img src={m.poster} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold truncate">{m.title} <span className="text-muted-foreground font-normal">— {m.titleEn}</span></div>
                    <div className="text-[11px] text-muted-foreground truncate">{m.type === "movie" ? "فيلم" : "مسلسل"} · {m.year} · {m.slug}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => loadForEdit(m.slug)}
                      className="p-2 rounded-lg bg-card border border-border hover:bg-accent" title="تعديل">
                      <Save className="h-4 w-4" />
                    </button>
                    <Link to={m.type === "movie" ? "/movie/$slug" : "/series/$slug"} params={{ slug: m.slug }}
                      className="p-2 rounded-lg bg-card border border-border hover:bg-accent" title="معاينة">
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                    <button onClick={() => deleteItem(m.slug)}
                      className="p-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground transition" title="حذف">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs sm:text-sm font-bold text-foreground">{children}</label>;
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <Label>{props.label}</Label>
      <input
        type={props.type ?? "text"}
        value={props.value}
        required={props.required}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </div>
  );
}

function PreviewCard({ item }: { item: CustomMovie }) {
  return (
    <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
      <h2 className="text-sm font-extrabold mb-3 inline-flex items-center gap-2">
        <Eye className="h-4 w-4 text-primary" /> معاينة قبل النشر
      </h2>
      <div className="rounded-xl overflow-hidden border border-border bg-background">
        <div className="relative h-32 sm:h-40 bg-muted">
          {item.backdrop ? <img src={item.backdrop} alt="" className="w-full h-full object-cover" /> : null}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        </div>
        <div className="p-4 grid grid-cols-[auto_minmax(0,1fr)] gap-3 -mt-12 relative">
          <div className="w-20 h-28 sm:w-24 sm:h-32 rounded-lg overflow-hidden bg-muted border border-border shrink-0">
            {item.poster ? <img src={item.poster} alt="" className="w-full h-full object-cover" /> : null}
          </div>
          <div className="min-w-0 pt-12">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-primary text-primary-foreground uppercase">
              {item.type === "movie" ? "فيلم" : "مسلسل"}
            </span>
            <h3 className="font-extrabold text-base sm:text-lg mt-1 truncate">{item.title || "—"}</h3>
            <p className="text-xs text-muted-foreground truncate">{item.titleEn}</p>
            <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-[var(--rating)] text-[var(--rating)]" /> {item.rating}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {item.year}</span>
              {item.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {item.duration}</span>}
            </div>
          </div>
        </div>
        <div className="px-4 pb-4">
          <p className="text-xs leading-relaxed text-muted-foreground">{item.overview}</p>
        </div>
        {item.article && item.article.length > 0 && (
          <div className="border-t border-border bg-background/40">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-4 pt-3">معاينة المقال</div>
            <ArticleRenderer article={item.article} className="!py-3" />
          </div>
        )}
      </div>
    </section>
  );
}

/* =================== Episodes tab =================== */

type WatchQualityDraft = { quality: string; url: string; token?: string };

function isValidHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function normalizeWatchQualities(rows: WatchQualityDraft[]): WatchQualityDraft[] {
  return rows
    .map((q) => ({
      quality: (q.quality || "auto").trim(),
      url: q.url.trim(),
      token: q.token?.trim() || undefined,
    }))
    .filter((q) => q.url || q.token);
}

function EpisodesTab() {
  const allItems = useAllItems();
  const series = useMemo(() => allItems.filter((m) => m.type === "series"), [allItems]);
  const [seriesSlug, setSeriesSlug] = useState(series[0]?.slug ?? "");
  useEffect(() => { if (!seriesSlug && series[0]) setSeriesSlug(series[0].slug); }, [series, seriesSlug]);

  useEpisodes();
  // Subscribe to override changes so saved edits (watch_urls, watch_token,
  // titles, thumbnails) show up immediately in the episode list instead of
  // appearing to "not save".
  const overridesMap = useEpisodeOverrides();
  // Stable signature so unrelated re-renders don't wipe in-progress local
  // edits inside EditableEpisodeRow (its useEffects sync from props).
  const overridesSig = useMemo(() => JSON.stringify(overridesMap), [overridesMap]);

  const contentSeasons = useMemo(
    () => (seriesSlug ? getMergedSeasonsForSeries(seriesSlug) : []),
    [seriesSlug, overridesSig],
  );

  // Season selector: existing seasons + "new" sentinel
  const NEW_SEASON = -1;
  const [seasonValue, setSeasonValue] = useState<number>(contentSeasons[0]?.season ?? NEW_SEASON);
  useEffect(() => {
    if (contentSeasons.length === 0) setSeasonValue(NEW_SEASON);
    else if (!contentSeasons.some((s) => s.season === seasonValue)) setSeasonValue(contentSeasons[0].season);
  }, [contentSeasons.length, seriesSlug]);

  // For "create new season" we let admin pick the season number.
  const suggestedNew = (contentSeasons.reduce((mx, s) => Math.max(mx, s.season), 0) || 0) + 1;
  const [newSeasonNum, setNewSeasonNum] = useState<number>(suggestedNew);
  useEffect(() => { setNewSeasonNum(suggestedNew); }, [suggestedNew]);

  const activeSeasonNum = seasonValue === NEW_SEASON ? newSeasonNum : seasonValue;
  const activeSeason = contentSeasons.find((x) => x.season === activeSeasonNum);

  // Publish state per season.
  const [pubBusy, setPubBusy] = useState<number | null>(null);
  const [pubStatus, setPubStatus] = useState<string | null>(null);

  // Shape of a single "extra" (draft or in-place) episode passed to the
  // season-payload builder. Includes optional watch_token + per-quality
  // watch_urls so the encrypted player can offer a quality picker.
  type ExtraEp = {
    episode_number: number;
    title: string;
    duration?: string;
    thumbnail?: string;
    watch_url?: string;
    download_url?: string;
    watch_token?: string;
    watch_urls?: { quality: string; url: string; token?: string }[];
    video_source?: { type: "external"|"mp4"|"m3u8"; source_id: string; protected: true };
  };

  // Build a season payload from canonical content + overrides (+ optional extra episodes).
  function buildSeasonPayload(seasonNum: number, extra: ReadonlyArray<ExtraEp> = []) {
    const rawSeason = getRawSeasonForSeries(seriesSlug, seasonNum);
    const base = getMergedSeasonsForSeries(seriesSlug).find((x) => x.season === seasonNum);
    const baseEps = base?.episodes ?? [];
    const merged = [...baseEps];
    for (const e of extra) {
      if (!merged.some((m) => m.episode_number === e.episode_number)) merged.push({ ...e, duration: e.duration ?? "", video_source: e.video_source ?? { type: "external" as const, source_id: `${seriesSlug}-s${seasonNum}e${e.episode_number}`, protected: true as const } });
    }
    merged.sort((a, b) => a.episode_number - b.episode_number);
    const rawEpisodes = Array.isArray((rawSeason as { episodes?: unknown })?.episodes)
      ? ((rawSeason as { episodes: Record<string, unknown>[] }).episodes)
      : [];
    return {
      ...(rawSeason ?? {}),
      season: seasonNum,
      episodes: merged.map((e) => {
        const rawEp = rawEpisodes.find((r) => r.episode_number === e.episode_number) ?? {};
        return {
          ...rawEp,
          episode_number: e.episode_number,
          title: e.title,
          duration: e.duration ?? "",
          ...(e.thumbnail ? { thumbnail: e.thumbnail } : {}),
          video_source: e.video_source ?? { type: "external", source_id: `${seriesSlug}-s${seasonNum}e${e.episode_number}`, protected: true as const },
          ...(e.watch_url ? { watch_url: e.watch_url } : {}),
          ...(e.watch_token ? { watch_token: e.watch_token } : {}),
          ...(Array.isArray((e as { watch_urls?: unknown }).watch_urls)
            ? { watch_urls: (e as { watch_urls: { quality: string; url: string; token?: string }[] }).watch_urls }
            : {}),
          ...(e.download_url ? { download_url: e.download_url } : {}),
        };
      }),
    };
  }

  async function publishSeasonOnly(seasonNum: number, extra: ReadonlyArray<ExtraEp> = []) {
    if (!seriesSlug) return;
    setPubBusy(seasonNum); setPubStatus(null);
    const payload = buildSeasonPayload(seasonNum, extra);
    const res = await uploadJsonToGithub({
      relativePath: `series/${seriesSlug}/season-${seasonNum}.json`,
      jsonContent: JSON.stringify(payload, null, 2),
      message: `chore(content): publish ${seriesSlug} season ${seasonNum}`,
    });
    setPubBusy(null);
    setPubStatus(res.ok ? `تمت إضافة الموسم ${seasonNum} إلى المسودّات ✓` : `فشل الحفظ: ${res.error}`);
    setTimeout(() => setPubStatus(null), 5000);
  }

  // === Draft (new episode) — stays local until "نشر إلى GitHub" is pressed ===
  type WatchQ = { quality: string; url: string; token?: string };
  type DraftEp = {
    episode_number: number;
    title: string;
    duration?: string;
    thumbnail?: string;
    watch_url?: string;
    download_url?: string;
    watch_token?: string;
    watch_urls?: WatchQ[];
    video_source?: { type: "external"|"mp4"|"m3u8"; source_id: string; protected: true };
  };
  const [draftEpisodes, setDraftEpisodes] = useState<DraftEp[]>([]);
  const nextEpisodeNumber = useMemo(() => {
    const baseMax = activeSeason?.episodes.reduce((mx, e) => Math.max(mx, e.episode_number), 0) ?? 0;
    const draftMax = draftEpisodes.reduce((mx, e) => Math.max(mx, e.episode_number), 0);
    return Math.max(baseMax, draftMax) + 1;
  }, [activeSeason?.episodes, draftEpisodes]);

  const [nTitle, setNTitle] = useState("");
  const [nWatch, setNWatch] = useState("");
  const [nDownload, setNDownload] = useState("");
  const [nThumb, setNThumb] = useState("");
  const [nDuration, setNDuration] = useState("");
  const [nWatchToken, setNWatchToken] = useState("");
  const [nWatchQualities, setNWatchQualities] = useState<WatchQ[]>([]);

  function addDraftEpisode(e: React.FormEvent) {
    e.preventDefault();
    if (!seriesSlug || !nTitle.trim()) return;
    const epNum = nextEpisodeNumber;
    const cleanQ = normalizeWatchQualities(nWatchQualities);
    const invalidQuality = cleanQ.find((q) => (q.url && !isValidHttpUrl(q.url)) || !q.token?.trim());
    if (invalidQuality) {
      toast.error("كل جودة مشاهدة محفوظة تحتاج watch_token غير فارغ، وأي رابط يجب أن يبدأ بـ http/https.");
      return;
    }
    setDraftEpisodes((arr) => [
      ...arr,
      {
        episode_number: epNum,
        title: nTitle.trim(),
        duration: nDuration.trim() || undefined,
        thumbnail: nThumb.trim() || undefined,
        watch_url: nWatch.trim() || undefined,
        watch_token: nWatchToken.trim() || undefined,
        watch_urls: cleanQ.length ? cleanQ : undefined,
        download_url: nDownload.trim() || undefined,
        video_source: { type: "external", source_id: `${seriesSlug}-s${activeSeasonNum}e${epNum}`, protected: true },
      },
    ]);
    setNTitle(""); setNWatch(""); setNDownload(""); setNThumb(""); setNDuration("");
    setNWatchToken(""); setNWatchQualities([]);
  }
  function removeDraft(num: number) {
    setDraftEpisodes((arr) => arr.filter((e) => e.episode_number !== num));
  }

  async function publishCurrentSeason() {
    await publishSeasonOnly(activeSeasonNum, draftEpisodes);
    setDraftEpisodes([]); // moved to canonical /content on next load
    // If this was a "new season" creation, switch selector to it.
    if (seasonValue === NEW_SEASON) setSeasonValue(activeSeasonNum);
  }

  if (!seriesSlug) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 text-center text-sm text-muted-foreground">
        لا توجد مسلسلات في المكتبة بعد. أضِف مسلسلاً من تبويب «المحتوى» أولاً.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* === Series + Season picker === */}
      <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>اختر المسلسل</Label>
            <select value={seriesSlug} onChange={(e) => setSeriesSlug(e.target.value)}
              className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {series.map((s) => (<option key={s.id} value={s.slug}>{s.title} — {s.titleEn}</option>))}
            </select>
          </div>

          <div>
            <Label>الموسم</Label>
            <select value={String(seasonValue)} onChange={(e) => setSeasonValue(parseInt(e.target.value, 10))}
              className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {contentSeasons.length === 0 && <option value={String(NEW_SEASON)}>— لا توجد مواسم بعد —</option>}
              {contentSeasons.map((s) => (
                <option key={s.season} value={String(s.season)}>الموسم {s.season} — {s.episodes.length} حلقة</option>
              ))}
              <option value={String(NEW_SEASON)}>➕ إنشاء موسم جديد…</option>
            </select>
          </div>

          {seasonValue === NEW_SEASON && (
            <div>
              <Label>رقم الموسم الجديد</Label>
              <input type="number" min={1} value={newSeasonNum}
                onChange={(e) => setNewSeasonNum(Math.max(1, parseInt(e.target.value || "1", 10)))}
                className="w-full mt-1.5 px-3 py-2.5 bg-background text-foreground border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                سيُنشأ ملف <code>series/{seriesSlug}/season-{newSeasonNum}.json</code> عند النشر.
              </p>
            </div>
          )}
        </div>

        {pubStatus && (
          <div className="mt-3 text-xs bg-primary/10 border border-primary/30 rounded-lg p-3 break-words">{pubStatus}</div>
        )}
      </section>

      {/* === Existing episodes (collapsible rows) === */}
      <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base sm:text-lg font-extrabold inline-flex items-center gap-2">
            <Film className="h-5 w-5 text-primary" /> الموسم {activeSeasonNum} — حلقات
          </h2>
          <Link to="/series/$slug" params={{ slug: seriesSlug }} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            عرض الصفحة <ExternalLink className="h-3 w-3" />
          </Link>
        </div>

        {activeSeason && activeSeason.episodes.length > 0 ? (
          <ul className="grid gap-2">
            {activeSeason.episodes.map((e) => (
              <EditableEpisodeRow
                key={`${activeSeasonNum}-${e.episode_number}`}
                slug={seriesSlug}
                season={activeSeasonNum}
                number={e.episode_number}
                title={localizeEpisodeTitle(e.title, "ar")}
                watchUrl={e.watch_url ?? ""}
                watchToken={e.watch_token ?? ""}
                watchUrls={e.watch_urls ?? []}
                downloadUrl={e.download_url ?? ""}
                thumbnail={e.thumbnail ?? ""}
                onPublish={() => publishSeasonOnly(activeSeasonNum)}
                publishing={pubBusy === activeSeasonNum}
              />
            ))}
          </ul>
        ) : (
          <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-xl">
            لا توجد حلقات في هذا الموسم بعد. أضِف الأولى من الأسفل.
          </div>
        )}

        {draftEpisodes.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-extrabold mb-2 text-muted-foreground">حلقات قيد الإضافة (لم تُنشر بعد)</h3>
            <ul className="grid gap-2">
              {draftEpisodes.map((e) => (
                <li key={e.episode_number} className="p-3 rounded-xl bg-background border border-dashed border-border flex items-center gap-3">
                  <span className="shrink-0 text-xs font-extrabold px-2.5 py-1 rounded-md text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
                    S{activeSeasonNum}·E{e.episode_number}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{e.title}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{e.watch_url || "— بدون رابط مشاهدة —"}</div>
                  </div>
                  <button onClick={() => removeDraft(e.episode_number)}
                    className="shrink-0 p-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground transition" aria-label="حذف">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={publishCurrentSeason}
            disabled={pubBusy === activeSeasonNum}
            className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-50">
            {pubBusy === activeSeasonNum ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            حفظ الموسم {activeSeasonNum} في المسودّات
          </button>
          <span className="text-[11px] text-muted-foreground">
            يُضاف ملف <code>season-{activeSeasonNum}.json</code> إلى قائمة المسودّات. للنشر الفعلي إلى GitHub اذهب إلى تبويب «المسودّات» واضغط «نشر الكل».
          </span>
        </div>
      </section>

      {/* === Add new episode === */}
      <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="h-5 w-5 text-primary" />
          <h2 className="text-base sm:text-lg font-extrabold">إضافة حلقة جديدة — الموسم {activeSeasonNum} · الحلقة {nextEpisodeNumber}</h2>
        </div>
        <form onSubmit={addDraftEpisode} className="grid md:grid-cols-2 gap-3">
          <Field label="عنوان الحلقة" value={nTitle} onChange={setNTitle} placeholder="مثال: البداية" />
          <Field label="المدّة (اختياري)" value={nDuration} onChange={setNDuration} placeholder="45m" />
          <Field label="رابط المشاهدة (احتياطي — يُستخدم إن غابت جودات المشاهدة)" value={nWatch} onChange={setNWatch} placeholder="https://… watch" />
          <div className="md:col-span-2">
            <label className="block text-[11px] font-bold text-muted-foreground mb-1">
              رمز التشفير (watch_token) — يُستخدم للتشغيل والتحميل عند غياب الروابط الصريحة
            </label>
            <textarea
              value={nWatchToken}
              onChange={(e) => setNWatchToken(e.target.value)}
              placeholder="LPSEdm8VkLgfodcO-uCH1ENfYcRa_q1BZtes_NiQ8wnBE3Y0..."
              rows={3}
              className="w-full text-xs font-mono px-3 py-2 rounded-lg bg-background border border-border focus:border-primary focus:outline-none break-all"
              dir="ltr"
              spellCheck={false}
            />
          </div>
          <div className="md:col-span-2">
            <WatchQualitiesEditor value={nWatchQualities} onChange={setNWatchQualities} />
          </div>
          <div className="md:col-span-2">
            <DownloadLinksEditor
              value={nDownload}
              onChange={setNDownload}
              label="روابط التحميل حسب الجودة (لهذه الحلقة)"
            />
          </div>
          <div className="md:col-span-2">
            <Field label="صورة مصغّرة" value={nThumb} onChange={setNThumb} placeholder="https://…/thumb.jpg" />
          </div>
          <div className="md:col-span-2 flex flex-wrap items-center gap-2">
            <button type="submit" className="btn-primary inline-flex items-center gap-2 text-sm">
              <Plus className="h-4 w-4" /> أضف للقائمة
            </button>
            <span className="text-[11px] text-muted-foreground">
              ستُضاف إلى المسوّدة المحلية. اضغط «حفظ الموسم في المسودّات» في الأعلى لإرسالها إلى قائمة المسودّات، ثم انشر الكل من تبويب «المسودّات».
            </span>
          </div>
        </form>
      </section>
    </div>
  );
}

function EditableEpisodeRow(props: {
  slug: string; season: number; number: number;
  title: string; watchUrl: string; watchToken?: string;
  watchUrls?: { quality: string; url: string; token?: string }[];
  downloadUrl: string; thumbnail: string;
  onPublish?: () => void | Promise<void>; publishing?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(props.title);
  const [watchUrl, setWatchUrl] = useState(props.watchUrl);
  const [watchToken, setWatchToken] = useState(props.watchToken ?? "");
  const [watchUrls, setWatchUrls] = useState<{ quality: string; url: string; token?: string }[]>(props.watchUrls ?? []);
  const [downloadUrl, setDownloadUrl] = useState(props.downloadUrl);
  const [thumb, setThumb] = useState(props.thumbnail);
  const [saved, setSaved] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  // Keep local state in sync ONLY when props actually change value.
  // Using `[props.watchUrls]` as a dep would fire on every parent
  // re-render (parent creates a fresh `[] ?? []` array each render),
  // which used to wipe newly-added watch qualities before save could run.
  useEffect(() => { setTitle(props.title); }, [props.title]);
  useEffect(() => { setWatchUrl(props.watchUrl); }, [props.watchUrl]);
  useEffect(() => { setWatchToken(props.watchToken ?? ""); }, [props.watchToken]);
  const propsWatchUrlsSig = JSON.stringify(props.watchUrls ?? []);
  useEffect(() => {
    const next = (props.watchUrls ?? []) as { quality: string; url: string; token?: string }[];
    setWatchUrls((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsWatchUrlsSig]);
  useEffect(() => { setDownloadUrl(props.downloadUrl); }, [props.downloadUrl]);
  useEffect(() => { setThumb(props.thumbnail); }, [props.thumbnail]);

  // ملقّم JSON لعرض الفرق (قبل/بعد) بحيث يتأكد المستخدم أن لا حقول
  // تُحذف عن غير قصد قبل الحفظ.
  const beforeJson = useMemo(() => JSON.stringify({
    title: props.title,
    watch_url: props.watchUrl || undefined,
    watch_token: props.watchToken || undefined,
    watch_urls: props.watchUrls?.length ? props.watchUrls : undefined,
    download_url: props.downloadUrl || undefined,
    thumbnail: props.thumbnail || undefined,
  }, null, 2), [props.title, props.watchUrl, props.watchToken, props.watchUrls, props.downloadUrl, props.thumbnail]);

  const afterJson = useMemo(() => {
    const cleanQ = normalizeWatchQualities(watchUrls);
    return JSON.stringify({
      title,
      watch_url: watchUrl || undefined,
      watch_token: watchToken || undefined,
      watch_urls: cleanQ.length ? cleanQ : undefined,
      download_url: downloadUrl || undefined,
      thumbnail: thumb || undefined,
    }, null, 2);
  }, [title, watchUrl, watchToken, watchUrls, downloadUrl, thumb]);

  const hasChanges = beforeJson !== afterJson;

  async function save() {
    const cleanQ = normalizeWatchQualities(watchUrls);
    const invalidQuality = cleanQ.find((q) => (q.url && !isValidHttpUrl(q.url)) || !q.token?.trim());
    if (invalidQuality) {
      toast.error("كل جودة مشاهدة محفوظة تحتاج watch_token غير فارغ، وأي رابط يجب أن يبدأ بـ http/https.");
      return;
    }
    // Persist the FULL current row state as an override — no "diff vs
    // props" trick. props can lag behind local state due to React batching,
    // so a diff-based patch used to drop a freshly-added watch quality
    // (the reported bug: add a quality → click save → 0 changes in draft).
    // Absolute writes eliminate the race entirely.
    const patch = cleanEpisodeOverride({
      title,
      watch_url: watchUrl,
      watch_token: watchToken,
      watch_urls: cleanQ,
      download_url: downloadUrl,
      thumbnail: thumb,
    });
    setOverride(props.slug, props.season, props.number, patch);
    try {
      await props.onPublish?.();
      toast.success(
        cleanQ.length
          ? `تم الحفظ في المسودّات — ${cleanQ.length} جودة مشاهدة.`
          : "تم الحفظ في المسودّات ✓",
      );
    } catch (e) {
      toast.error(`فشل حفظ المسودّة: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }
  function resetRow() {
    clearOverride(props.slug, props.season, props.number);
  }

  return (
    <li className="rounded-xl bg-background border border-border overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full text-start flex items-center gap-2 p-3 hover:bg-accent/40 transition">
        <span className="shrink-0 text-xs font-extrabold px-2.5 py-1 rounded-md text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
          S{props.season}·E{props.number}
        </span>
        <span className="flex-1 font-bold truncate">{title || props.title || "—"}</span>
        <span className="text-[11px] text-muted-foreground hidden sm:inline truncate max-w-[40%]">{watchUrl || (watchUrls.length ? `${watchUrls.length} جودة مشاهدة` : "بدون رابط مشاهدة")}</span>
        <span className={`shrink-0 text-xs transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="p-3 pt-0 border-t border-border">
          <div className="mb-2">
            <Field label="عنوان الحلقة" value={title} onChange={setTitle} placeholder="عنوان الحلقة" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="رابط المشاهدة (احتياطي)" value={watchUrl} onChange={setWatchUrl} placeholder="https://… watch" />
            <div>
              <label className="block text-[11px] font-bold text-muted-foreground mb-1">
                رابط التحميل (متعدد الجودات)
              </label>
              <div className="rounded-lg border border-border bg-card p-2">
                <DownloadLinksEditor
                  value={downloadUrl}
                  onChange={setDownloadUrl}
                  compact
                />
              </div>
            </div>
          </div>
          <div className="mt-2">
            <WatchQualitiesEditor value={watchUrls} onChange={setWatchUrls} />
          </div>
          <div className="mt-2">
            <label className="block text-[11px] font-bold text-muted-foreground mb-1">
              رمز التشفير (watch_token) — يُستخدم للتشغيل والتحميل عند غياب الروابط الصريحة
            </label>
            <textarea
              value={watchToken}
              onChange={(e) => setWatchToken(e.target.value)}
              placeholder="LPSEdm8VkLgfodcO-uCH1ENfYcRa_q1BZtes_NiQ8wnBE3Y0..."
              rows={3}
              className="w-full text-xs font-mono px-3 py-2 rounded-lg bg-background border border-border focus:border-primary focus:outline-none break-all"
              dir="ltr"
              spellCheck={false}
            />
          </div>
          <div className="mt-2">
            <Field label="صورة مصغّرة" value={thumb} onChange={setThumb} placeholder="https://…/thumb.jpg" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={save} disabled={!!props.publishing} className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> حفظ التعديل
            </button>
            {props.onPublish && (
              <button onClick={save} disabled={!!props.publishing}
                className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground text-xs disabled:opacity-50">
                {props.publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                حفظ هذا التعديل في المسودّات
              </button>
            )}
            <button onClick={resetRow} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-card border border-border hover:bg-accent text-xs">
              استرجاع الأصلي
            </button>
            <button type="button" onClick={() => setShowDiff((v) => !v)}
              className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs border transition ${
                hasChanges
                  ? "bg-amber-500/10 border-amber-500/40 text-amber-600 hover:bg-amber-500/20"
                  : "bg-card border-border text-muted-foreground"
              }`}
              title="مقارنة الحقول قبل/بعد لضمان عدم فقدان أي بيانات">
              <GitCompare className="h-3.5 w-3.5" />
              {showDiff ? "إخفاء مقارنة JSON" : hasChanges ? "معاينة الفرق (قبل/بعد)" : "لا تغييرات"}
            </button>
            {saved && <span className="text-xs text-primary">تم الحفظ ✓</span>}
          </div>

          {showDiff && (
            <div className="mt-3 rounded-lg border border-border bg-background overflow-hidden">
              <div className="px-3 py-2 border-b border-border bg-card text-[11px] font-extrabold inline-flex items-center gap-1.5">
                <GitCompare className="h-3.5 w-3.5 text-primary" />
                مقارنة JSON — قبل/بعد (تحقّق من عدم حذف أي حقول)
              </div>
              <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
                <div className="p-2">
                  <div className="text-[10.5px] font-bold text-muted-foreground mb-1">قبل (المحفوظ حالياً)</div>
                  <pre dir="ltr" className="text-[11px] font-mono whitespace-pre-wrap break-all bg-card border border-border rounded-md p-2 max-h-64 overflow-auto">
{beforeJson}
                  </pre>
                </div>
                <div className="p-2">
                  <div className="text-[10.5px] font-bold text-muted-foreground mb-1">بعد (سيُحفَظ)</div>
                  <pre dir="ltr" className={`text-[11px] font-mono whitespace-pre-wrap break-all border rounded-md p-2 max-h-64 overflow-auto ${
                    hasChanges ? "bg-emerald-500/5 border-emerald-500/30" : "bg-card border-border"
                  }`}>
{afterJson}
                  </pre>
                </div>
              </div>
              {hasChanges && (
                <div className="px-3 py-2 border-t border-border bg-amber-500/5 text-[11px] text-amber-700 inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3" />
                  توجد تغييرات — راجعها ثم اضغط «حفظ التعديل».
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * WatchQualitiesEditor — builder for per-quality watch links on an
 * episode. Each row: quality + url + optional per-quality watch_token.
 * The player renders one button per row after the viewer clicks Watch.
 */
function WatchQualitiesEditor(props: {
  value: { quality: string; url: string; token?: string }[];
  onChange: (rows: { quality: string; url: string; token?: string }[]) => void;
}) {
  const rows = props.value;
  const [previewIdx, setPreviewIdx] = useState<number>(0);
  const update = (next: { quality: string; url: string; token?: string }[]) => {
    props.onChange(next);
    if (previewIdx >= next.length) setPreviewIdx(Math.max(0, next.length - 1));
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
    if (previewIdx === i) setPreviewIdx(j);
    else if (previewIdx === j) setPreviewIdx(i);
  };

  const selected = rows[previewIdx];
  const previewValid =
    !!selected &&
    !!selected.token?.trim() &&
    (!selected.url.trim() || isValidHttpUrl(selected.url));

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-primary inline-flex items-center gap-1.5">
          <Play className="h-3.5 w-3.5" />
          روابط المشاهدة حسب الجودة — كل جودة تظهر كزر في مشغّل الحلقة
        </label>
        <button
          type="button"
          onClick={() => update([...rows, { quality: rows.length === 0 ? "1080p" : "720p", url: "" }])}
          className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-2 py-1 text-[11px] font-bold hover:opacity-90"
        >
          <Plus className="h-3 w-3" /> أضف جودة مشاهدة
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-background p-3 text-[11px] text-muted-foreground text-center">
          لا توجد جودات مشاهدة. أضف واحدة أو أكثر (480p / 720p / 1080p / 4K)، وستظهر كأزرار في مشغّل الحلقة.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const url = r.url.trim();
            const token = r.token?.trim() ?? "";
            const rowValid = !!token && (!url || isValidHttpUrl(url));
            const isPreview = i === previewIdx;
            return (
              <div
                key={i}
                className={`grid gap-1.5 sm:grid-cols-[110px_1fr_auto_auto_auto_auto] rounded-md border p-2 ${
                  isPreview ? "border-primary bg-primary/5" : "border-border bg-background"
                }`}
              >
                <input
                  value={r.quality}
                  onChange={(e) => update(rows.map((x, ix) => (ix === i ? { ...x, quality: e.target.value } : x)))}
                  placeholder="1080p"
                  className="px-2 py-1.5 bg-card border border-border rounded-md text-xs font-bold"
                />
                <input
                  value={r.url}
                  onChange={(e) => update(rows.map((x, ix) => (ix === i ? { ...x, url: e.target.value } : x)))}
                  placeholder="https://…/watch"
                  dir="ltr"
                  className="px-2 py-1.5 bg-card border border-border rounded-md text-xs"
                />
                <button
                  type="button"
                  onClick={() => setPreviewIdx(i)}
                  title="اختَر لعرض المعاينة"
                  aria-pressed={isPreview}
                  className={`h-[30px] w-[34px] inline-flex items-center justify-center rounded-md border transition ${
                    isPreview
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-muted-foreground border-border hover:bg-accent"
                  }`}
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  title="تحريك للأعلى"
                  className="h-[30px] w-[34px] inline-flex items-center justify-center rounded-md bg-card text-foreground border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, +1)}
                  disabled={i === rows.length - 1}
                  title="تحريك للأسفل"
                  className="h-[30px] w-[34px] inline-flex items-center justify-center rounded-md bg-card text-foreground border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => update(rows.filter((_, ix) => ix !== i))}
                  className="h-[30px] w-[34px] inline-flex items-center justify-center rounded-md bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground transition"
                  aria-label="حذف الجودة"
                  title="حذف الجودة"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <textarea
                  value={r.token ?? ""}
                  onChange={(e) => update(rows.map((x, ix) => (ix === i ? { ...x, token: e.target.value } : x)))}
                  placeholder="watch_token خاص بهذه الجودة (اختياري)"
                  rows={2}
                  dir="ltr"
                  className="sm:col-span-6 px-2 py-1.5 bg-card border border-border rounded-md text-[11px] font-mono break-all"
                />
                {!rowValid && (
                  <div className="sm:col-span-6 text-[10.5px] text-amber-600 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> هذه الجودة لن تُحفَظ بشكل صحيح — أضف watch_token، وإذا أضفت رابطاً يجب أن يبدأ بـ http/https.
                  </div>
                )}
              </div>
            );
          })}

          {/* Live preview: which quality will play & what token/URL will be used */}
          <div className="rounded-md border border-primary/40 bg-background p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Eye className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] font-extrabold text-primary">معاينة قبل الحفظ — الجودة التي ستُشغَّل الآن</span>
            </div>
            {selected ? (
              <div className="text-[11px] space-y-1">
                <div>
                  <span className="text-muted-foreground">الجودة: </span>
                  <span className="font-extrabold">{selected.quality || "—"}</span>
                  {!previewValid && (
                    <span className="ms-2 text-destructive font-bold">⚠️ غير صالحة</span>
                  )}
                </div>
                <div dir="ltr" className="font-mono break-all">
                  <span className="text-muted-foreground">watch_url: </span>
                  <span className={!selected.url.trim() || isValidHttpUrl(selected.url) ? "text-emerald-600" : "text-destructive"}>
                    {selected.url || "(فارغ — سيتم الاعتماد على watch_token)"}
                  </span>
                </div>
                <div dir="ltr" className="font-mono break-all">
                  <span className="text-muted-foreground">watch_token: </span>
                  <span className={selected.token && selected.token.trim() ? "text-emerald-600" : "text-muted-foreground"}>
                    {selected.token && selected.token.trim() ? `${selected.token.slice(0, 32)}${selected.token.length > 32 ? "…" : ""}` : "(فارغ — مطلوب لكل جودة مشاهدة)"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground">لا توجد جودة محدّدة للمعاينة.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* =================== Data tab =================== */

function DataTab() {
  const [msg, setMsg] = useState<string | null>(null);

  function download() {
    // Full backup — everything that lives in localStorage + canonical content.
    const ls = (k: string, fallback: unknown = null) => {
      try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
    };
    const baseJson = exportLibraryJSON({
      episodes: ls("rawan-episodes-v1", []),
      episodeOverrides: ls("rawan-episode-overrides-v1", {}),
      socialLinks: ls("rawan-social-links-v1", {}),
      adConfig: ls("rawan:ad-config:v1", {}),
      siteAds: ls("rawan:site-ads:v1", {}),
      newsletter: ls("rawan-newsletter-v1", []),
      stories: ls("rawan-stories-v1", []),
      ghHistory: ls("rawan-gh-history", []),
    });
    // Inject canonical /content snapshot so the backup is portable.
    let parsed: Record<string, unknown> = {};
    try { parsed = JSON.parse(baseJson) as Record<string, unknown>; } catch { /* keep flat */ }
    try {
      const movies = getAllMovies();
      const seriesInfos = getAllSeries();
      const seasons: Record<string, ContentSeason[]> = {};
      for (const sx of seriesInfos) seasons[sx.slug] = getSeasonsRaw(sx.slug);
      parsed.content = { movies, seriesInfos, seasons };
    } catch (e) { /* ignore */ }
    parsed.exportedAt = new Date().toISOString();
    parsed.version = 2;
    const json = JSON.stringify(parsed, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rawan-library-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setMsg("تم تنزيل النسخة الاحتياطية الكاملة ✓");
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const raw = await file.text();
      const { added } = importLibraryJSON(raw);
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const restore = (k: string, src: string) => {
        if (parsed[src] !== undefined) localStorage.setItem(k, JSON.stringify(parsed[src]));
      };
      restore("rawan-episodes-v1", "episodes");
      restore("rawan-episode-overrides-v1", "episodeOverrides");
      restore("rawan-social-links-v1", "socialLinks");
      restore("rawan:ad-config:v1", "adConfig");
      restore("rawan:site-ads:v1", "siteAds");
      restore("rawan-newsletter-v1", "newsletter");
      restore("rawan-stories-v1", "stories");
      restore("rawan-gh-history", "ghHistory");
      setMsg(`تم استيراد ${added} عنصراً + إعدادات الموقع ✓`);
    } catch (err) {
      setMsg(`فشل الاستيراد: ${(err as Error).message}`);
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <section className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h2 className="text-base sm:text-lg font-extrabold inline-flex items-center gap-2 mb-3">
          <Download className="h-5 w-5 text-primary" /> تصدير المكتبة
        </h2>
        <p className="text-sm text-muted-foreground">يحفظ جميع الأفلام والمسلسلات والحلقات المنشورة في ملف JSON واحد.</p>
        <button onClick={download} className="btn-primary inline-flex items-center gap-2 mt-4">
          <Download className="h-4 w-4" /> تنزيل ملف JSON
        </button>
      </section>
      <section className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h2 className="text-base sm:text-lg font-extrabold inline-flex items-center gap-2 mb-3">
          <Upload className="h-5 w-5 text-primary" /> استيراد المكتبة
        </h2>
        <p className="text-sm text-muted-foreground">حمّل ملف JSON أُنشئ سابقاً لاستعادة المحتوى.</p>
        <label className="btn-primary inline-flex items-center gap-2 mt-4 cursor-pointer">
          <Upload className="h-4 w-4" /> اختر ملفاً
          <input type="file" accept="application/json" onChange={onFile} className="hidden" />
        </label>
      </section>
      {msg && (
        <div className="md:col-span-2 text-sm bg-primary/10 border border-primary/30 text-foreground rounded-xl p-3">
          {msg}
        </div>
      )}
    </div>
  );
}

/* =================== Publish JSON → GitHub tab =================== */

/**
 * Serialize a dashboard item into the exact JSON shape stored under
 * /content/movies/<slug>.json or /content/series/<slug>/info.json.
 *
 * Unified watch/download wiring (single source of truth):
 *   - `downloads[]` is the only editable list (kind: "watch" | "download").
 *   - The first entry whose kind === "watch" (fallback: first link of any
 *     kind) is mirrored into the canonical `watch_url` field so JSON in
 *     /content always matches the folder convention used by the loader
 *     and RawanPlayer — no drift between dashboard and repo.
 *   - `watch_token` (pre-encrypted, optional) is preserved verbatim so
 *     the player skips re-encryption per click.
 */
function toMovieJson(m: CustomMovie): { json: string; relPath: string } {
  const isSeries = m.type === "series";
  const parsedSeasons = (() => {
    if (typeof m.seasonsCount === "number" && m.seasonsCount > 0) return m.seasonsCount;
    const match = String(m.duration ?? "").match(/(\d+)/);
    return match ? Math.max(1, parseInt(match[1], 10)) : 1;
  })();
  const videoSource = m.videoSource ?? {
    type: "external" as const,
    source_id: m.slug,
    protected: true as const,
  };
  const downloads = m.downloads ?? [];
  const primaryWatch =
    downloads.find((d) => d.kind === "watch" && d.url) ??
    downloads.find((d) => !!d.url);

  // Start from the raw on-disk JSON so we NEVER strip a field that the
  // dashboard doesn't render. Dashboard edits then overlay on top.
  const base: Record<string, unknown> = { ...(m.__raw ?? {}) };

  // Dashboard-controlled overlay. `undefined` values leave the raw field
  // untouched; explicit values (including empty arrays / strings) win.
  const overlay: Record<string, unknown> = {
    id: m.id,
    slug: m.slug,
    title: m.title,
    title_fr: m.titleFr || undefined,
    original_title: m.titleEn,
    description: m.overview,
    description_en: m.overviewEn,
    description_fr: m.overviewFr || undefined,
    year: m.year,
    duration: m.duration,
    duration_minutes: m.durationMinutes,
    release_date: m.releaseDate,
    country: m.country,
    genres: m.genresEn,
    genres_ar: m.genres,
    genres_fr: m.genresFr && m.genresFr.length ? m.genresFr : undefined,
    rating: m.rating,
    cast: m.cast,
    director: m.director,
    poster: m.poster,
    backdrop: m.backdrop,
    trailer_url: m.trailer || undefined,
    tags: m.tags ?? [],
    is_trending: !!(m as { isTrending?: boolean }).isTrending,
    is_new: !!m.isNew,
    // Only overwrite article/paragraphs when the admin actually has
    // content — otherwise keep whatever the raw JSON already carries so
    // published articles are never wiped by an in-browser edit that
    // never loaded the raw article list.
    ...(Array.isArray(m.article) && m.article.length ? { article: m.article } : {}),
    ...(Array.isArray(m.paragraphs) && m.paragraphs.length ? { paragraphs: m.paragraphs } : {}),
    downloads,
    watch_url: primaryWatch?.url || undefined,
    watch_token: m.watchToken || undefined,
  };
  if (isSeries) {
    overlay.seasons_count = parsedSeasons;
    overlay.first_air_date = m.firstAirDate;
    overlay.last_air_date = m.lastAirDate;
    if (m.status) overlay.status = m.status;
    if (m.statusEn) overlay.status_en = m.statusEn;
    if (m.statusFr) overlay.status_fr = m.statusFr;
  } else {
    overlay.video_source = videoSource;
  }

  // Merge: keep raw fields intact, apply overlay only where defined.
  const obj: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(overlay)) {
    if (v !== undefined) obj[k] = v;
  }
  // Strip our internal marker if it ever leaked into raw.
  delete (obj as { __raw?: unknown }).__raw;

  const folder = isSeries ? `series/${m.slug}/info` : `movies/${m.slug}`;
  return { json: JSON.stringify(obj, null, 2), relPath: `${folder}.json` };
}

function PublishTab() {
  const items = useAllItems();
  const customs = useCustomItems();
  const allCandidates = [...customs, ...items.filter((i) => !customs.some((c) => c.slug === i.slug))] as CustomMovie[];

  const [cfg, setCfg] = useState<GitHubConfig>(() => getGitHubConfig() ?? {
    token: "", repo: "", branch: "main", basePath: "content",
  });
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  type PubLog = { slug: string; title: string; url: string; at: number };
  const [history, setHistory] = useState<PubLog[]>(() => {
    if (typeof window === "undefined") return [];
    try { return JSON.parse(localStorage.getItem("rawan-gh-history") || "[]") as PubLog[]; } catch { return []; }
  });
  function pushHistory(entry: PubLog) {
    const next = [entry, ...history].slice(0, 10);
    setHistory(next);
    localStorage.setItem("rawan-gh-history", JSON.stringify(next));
  }

  const selected = allCandidates.find((i) => i.slug === selectedSlug) ?? null;

  function saveCfg() {
    saveGitHubConfig(cfg);
    setStatus("تم حفظ الإعدادات محلياً ✓");
    setTimeout(() => setStatus(null), 1800);
  }
  function clearCfg() {
    clearGitHubConfig();
    setCfg({ token: "", repo: "", branch: "main", basePath: "content" });
    setStatus("تم مسح الإعدادات.");
    setTimeout(() => setStatus(null), 1800);
  }

  async function testConnection() {
    if (!cfg.token || !cfg.repo) { setStatus("أدخل التوكن والمستودع أولاً."); return; }
    const repoTrim = cfg.repo.trim().replace(/^\/+|\/+$/g, "").replace(/^https?:\/\/github\.com\//i, "");
    if (!/^[^/\s]+\/[^/\s]+$/.test(repoTrim)) {
      setStatus(`صيغة المستودع غير صحيحة. استخدم owner/repo (مثل: elhizazi/rawan-ui). الحالي: "${cfg.repo}"`);
      return;
    }
    setTesting(true); setStatus(null);
    const headers = { Authorization: `Bearer ${cfg.token}`, Accept: "application/vnd.github+json", "X-GitHub-Api-Version": "2022-11-28" };
    try {
      // 1) تحقق من صلاحية التوكن نفسه
      const who = await fetch("https://api.github.com/user", { headers });
      if (who.status === 401) {
        setStatus("التوكن غير صالح أو منتهي الصلاحية (401). أنشئ توكن جديد من GitHub.");
        return;
      }
      const whoJ = who.ok ? (await who.json()) as { login?: string } : null;

      // 2) تحقق من الوصول للمستودع
      const res = await fetch(`https://api.github.com/repos/${repoTrim}`, { headers });
      if (res.ok) {
        const j = await res.json() as { full_name: string; default_branch: string; private: boolean };
        if (repoTrim !== cfg.repo) {
          const next = { ...cfg, repo: repoTrim };
          setCfg(next); saveGitHubConfig(next);
        }
        setStatus(`اتصال ناجح ✓ — ${j.full_name} (${j.private ? "خاص" : "عام"}) · الفرع الافتراضي: ${j.default_branch}${whoJ?.login ? ` · بصفة @${whoJ.login}` : ""}`);
        return;
      }
      if (res.status === 404) {
        setStatus(
          `لم يُعثر على المستودع "${repoTrim}" (404). للمستودعات الخاصة الأسباب الشائعة:\n` +
          `• اسم المستودع/المالك مكتوب خطأ (حساس لحالة الأحرف).\n` +
          `• إن كنت تستخدم Fine-grained token: يجب اختيار هذا المستودع تحديداً في "Repository access"، ومنحه صلاحيتي Contents: Read & write + Metadata: Read.\n` +
          `• إن كنت تستخدم Classic token: يجب تفعيل صلاحية repo الكاملة (وليس public_repo فقط) للوصول للمستودعات الخاصة.\n` +
          `• إن كان المستودع داخل منظمة (Organization): فعّل SSO على التوكن من صفحة التوكنات.${whoJ?.login ? ` (التوكن صالح بصفة @${whoJ.login})` : ""}`
        );
        return;
      }
      if (res.status === 403) {
        setStatus("الوصول مرفوض (403): التوكن صالح لكن لا يملك صلاحية على هذا المستودع. راجع Repository access و Permissions في صفحة التوكن.");
        return;
      }
      const text = await res.text().catch(() => "");
      setStatus(`فشل الاتصال (${res.status}): ${text.slice(0, 200)}`);
    } catch (e) {
      setStatus(`خطأ شبكة: ${(e as Error).message}`);
    } finally {
      setTesting(false);
    }
  }

  function downloadJsonOnly() {
    if (!selected) return;
    const { json, relPath } = toMovieJson(selected);
    const blob = new Blob([json], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = relPath.replace(/\//g, "_");
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function uploadToGithub() {
    if (!selected) return;
    setBusy(true); setStatus(null);
    const { json, relPath } = toMovieJson(selected);
    const res = await uploadJsonToGithub({
      relativePath: relPath,
      jsonContent: json,
      message: `chore(content): publish ${selected.title} (${selected.slug})`,
    });
    setBusy(false);
    if (res.ok) {
      setStatus(`تم الرفع بنجاح ✓ — ${res.url}`);
      pushHistory({ slug: selected.slug, title: selected.title, url: res.url, at: Date.now() });
    } else {
      setStatus(`فشل الرفع: ${res.error}`);
    }
  }

  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-5">
      {/* GitHub config */}
      <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h2 className="text-lg font-extrabold mb-3 flex items-center gap-2">
          <Upload className="h-5 w-5 text-primary" />
          إعدادات GitHub
        </h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          هذه القيم تُحفظ في متصفح المدير فقط (localStorage). استخدم PAT بصلاحية
          <span className="font-mono"> Contents: read &amp; write </span>
          على المستودع المستهدف.
        </p>

        <div className="grid gap-3">
          <ConfigInput label="GitHub PAT (سري)" type="password" value={cfg.token}
            onChange={(v) => setCfg({ ...cfg, token: v })} placeholder="github_pat_xxx…" />
          <ConfigInput label="المستودع (owner/repo)" value={cfg.repo}
            onChange={(v) => setCfg({ ...cfg, repo: v })} placeholder="username/rawanui-content" />
          <ConfigInput label="الفرع (branch)" value={cfg.branch}
            onChange={(v) => setCfg({ ...cfg, branch: v })} placeholder="main" />
          <ConfigInput label="مسار الأساس داخل المستودع" value={cfg.basePath}
            onChange={(v) => setCfg({ ...cfg, basePath: v })} placeholder="content" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={saveCfg} className="btn-primary inline-flex items-center gap-2">
            <Save className="h-4 w-4" /> حفظ الإعدادات
          </button>
          <button onClick={testConnection} disabled={testing} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-sm disabled:opacity-50">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            اختبار الاتصال
          </button>
          <button onClick={clearCfg} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-sm">
            <Trash2 className="h-4 w-4" /> مسح
          </button>
        </div>
      </div>


      {/* Item picker + actions */}
      <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h2 className="text-lg font-extrabold mb-3 flex items-center gap-2">
          <Film className="h-5 w-5 text-primary" />
          اختر فيلماً/مسلسلاً للنشر
        </h2>
        <select
          value={selectedSlug}
          onChange={(e) => setSelectedSlug(e.target.value)}
          className="w-full mt-1.5 px-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">— اختر —</option>
          {allCandidates.map((i) => (
            <option key={i.slug} value={i.slug}>
              {i.type === "series" ? "[مسلسل]" : "[فيلم]"} {i.title} — {i.titleEn}
            </option>
          ))}
        </select>

        {selected && (() => {
          const { relPath } = toMovieJson(selected);
          return (
            <div className="mt-4 p-3 rounded-xl bg-background border border-border">
              <div className="text-xs text-muted-foreground">سيُنشأ الملف في:</div>
              <div className="font-mono text-sm break-all">
                <span className="text-muted-foreground">{cfg.basePath || "content"}/</span>
                <span className="text-primary">{relPath}</span>
              </div>
            </div>
          );
        })()}

        <div className="mt-4 flex flex-wrap gap-2">
          <button disabled={!selected} onClick={downloadJsonOnly}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-sm disabled:opacity-50">
            <Download className="h-4 w-4" /> تنزيل JSON فقط
          </button>
          <button disabled={!selected || busy || !cfg.token || !cfg.repo} onClick={uploadToGithub}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Upload className="h-4 w-4" /> {busy ? "جارٍ الرفع…" : "رفع تلقائي إلى GitHub"}
          </button>
        </div>

        {status && (
          <div className="mt-4 text-sm bg-background border border-border rounded-lg p-3 break-words whitespace-pre-line leading-relaxed">
            {status}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-extrabold mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" /> سجل آخر عمليات النشر
            </h3>
            <ul className="grid gap-1.5">
              {history.map((h, i) => (
                <li key={i} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 p-2 rounded-lg bg-background border border-border text-xs">
                  <div className="min-w-0">
                    <div className="truncate font-bold">{h.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{new Date(h.at).toLocaleString("ar-MA")}</div>
                  </div>
                  <a href={h.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                    عرض <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}



        <details className="mt-5 text-xs text-muted-foreground">
          <summary className="cursor-pointer font-bold">طريقة الإعداد اليدوية</summary>
          <ol className="list-decimal list-inside mt-2 space-y-1 leading-relaxed">
            <li>أنشئ Fine-grained PAT في GitHub بصلاحية <span className="font-mono">Contents: read &amp; write</span> على المستودع المستهدف.</li>
            <li>ضع المستودع <span className="font-mono">owner/repo</span> والفرع والمسار (افتراضياً <span className="font-mono">content</span>).</li>
            <li>اضغط "حفظ الإعدادات" ثم اختر العنصر واضغط "رفع تلقائي".</li>
            <li>أو استخدم "تنزيل JSON فقط" وارفعه يدوياً إلى المسار المعروض.</li>
          </ol>
        </details>
      </div>
    </div>
  );
}

function ConfigInput({ label, value, onChange, type = "text", placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && visible ? "text" : type;
  const canCopy = isPassword && !!value;
  const padEnd = isPassword ? (canCopy ? "pe-24" : "pe-12") : "";

  async function handleCopy() {
    if (!value) return;
    let ok = false;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText && window.isSecureContext !== false) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch { /* fall through to fallback */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, value.length);
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      toast.success("تم النسخ إلى الحافظة");
      setTimeout(() => setCopied(false), 1400);
    } else {
      toast.error("تعذّر النسخ — انسخ يدويًا");
    }
  }

  return (
    <div>
      <label className="text-xs font-bold">{label}</label>
      <div className="relative mt-1">
        <input
          type={effectiveType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-2 bg-background border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary ${padEnd}`}
        />
        {isPassword && (
          <div className="absolute inset-y-0 end-1 flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "إخفاء" : "إظهار"}
              title={visible ? "إخفاء" : "إظهار"}
              className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition"
            >
              {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            {canCopy && (
              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? "تم النسخ" : "نسخ"}
                title={copied ? "تم النسخ" : "نسخ"}
                className={`h-8 w-8 grid place-items-center rounded-md transition ${
                  copied
                    ? "text-emerald-600 bg-emerald-500/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* =================== Comments archive tab =================== */

function CommentsArchiveTab() {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(null);
  const [result, setResult] = useState<ArchiveResult | null>(null);
  const [stats, setStats] = useState<{ totalThreads: number; totalComments: number } | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsErr, setStatsErr] = useState<string | null>(null);
  const [purgeDays, setPurgeDays] = useState(180);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState<string | null>(null);

  async function refreshStats() {
    setStatsLoading(true);
    setStatsErr(null);
    try {
      const all = await fetchAllComments();
      const { summary } = groupAndSummarize(all);
      setStats({ totalThreads: summary.totalThreads, totalComments: summary.totalComments });
    } catch (e) {
      setStatsErr((e as Error).message);
    } finally {
      setStatsLoading(false);
    }
  }

  useEffect(() => { void refreshStats(); }, []);

  async function archiveNow() {
    setBusy(true);
    setProgress({ done: 0, total: 0, current: "" });
    setResult(null);
    try {
      const res = await archiveCommentsToGitHub((done, total, threadId) =>
        setProgress({ done, total, current: threadId }),
      );
      setResult(res);
      await refreshStats();
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function downloadOnly() {
    setBusy(true);
    try {
      const all = await fetchAllComments();
      const { grouped, summary } = groupAndSummarize(all);
      downloadArchiveBundle({
        ok: true,
        uploaded: 0,
        skipped: 0,
        errors: [],
        summary,
        data: grouped,
      });
    } finally {
      setBusy(false);
    }
  }

  async function purgeOld() {
    if (!result) {
      setPurgeMsg("ابدأ بالأرشفة أوّلًا (تنظيف يحذف فقط تعليقات تم رفعها إلى GitHub).");
      return;
    }
    if (!confirm(`سيتم حذف التعليقات الأقدم من ${purgeDays} يومًا من Firestore (موجودة بالفعل في أرشيف GitHub). متابعة؟`)) return;
    setPurgeBusy(true);
    setPurgeMsg(null);
    try {
      const archivedIds = new Set<string>();
      for (const arr of Object.values(result.data)) for (const c of arr) archivedIds.add(c.id);
      const cutoff = Date.now() - purgeDays * 86400_000;
      const out = await purgeArchivedCommentsOlderThan(cutoff, archivedIds);
      setPurgeMsg(`تم حذف ${out.deleted} تعليق${out.errors.length ? ` — ${out.errors.length} خطأ` : ""}.`);
      await refreshStats();
    } finally {
      setPurgeBusy(false);
    }
  }

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-5">
      <section className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h2 className="text-base sm:text-lg font-extrabold inline-flex items-center gap-2 mb-3">
          <Archive className="h-5 w-5 text-primary" /> أرشفة التعليقات إلى GitHub
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          يُبقي Firestore كمصدر حقيقي realtime للتعليقات، ويصدّر نسخة JSON منظّمة
          داخل مستودع GitHub المضبوط في تبويب «نشر JSON إلى GitHub». الهيكل:
        </p>
        <pre className="mt-3 text-[11px] bg-background border border-border rounded-lg p-3 overflow-x-auto leading-relaxed">{`comments-archive/
  index.json
  threads/<threadId>.json   ← تعليقات كل فيلم/مسلسل/حلقة`}</pre>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border bg-background p-3">
            <div className="text-[11px] text-muted-foreground">المواضيع</div>
            <div className="text-xl font-extrabold">
              {statsLoading ? <Loader2 className="h-4 w-4 inline animate-spin" /> : stats?.totalThreads ?? "—"}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-background p-3">
            <div className="text-[11px] text-muted-foreground">إجمالي التعليقات</div>
            <div className="text-xl font-extrabold">
              {statsLoading ? <Loader2 className="h-4 w-4 inline animate-spin" /> : stats?.totalComments ?? "—"}
            </div>
          </div>
        </div>
        {statsErr && <div className="mt-2 text-xs text-destructive">{statsErr}</div>}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={archiveNow}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            أرشفة الآن إلى GitHub
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={downloadOnly}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-sm disabled:opacity-60"
          >
            <Download className="h-4 w-4" /> تنزيل JSON محليًا
          </button>
          <button
            type="button"
            disabled={statsLoading}
            onClick={() => void refreshStats()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-sm disabled:opacity-60"
          >
            تحديث العدّاد
          </button>
        </div>

        {progress && (
          <div className="mt-4 text-xs text-muted-foreground">
            جارٍ رفع <span className="font-mono">{progress.current || "…"}</span> ({progress.done}/{progress.total || "?"})
          </div>
        )}

        {result && (
          <div className="mt-4 text-xs bg-background border border-border rounded-lg p-3 break-words leading-relaxed">
            <div className={result.ok ? "text-green-600 font-bold" : "text-destructive font-bold"}>
              {result.ok ? "✓ تمت الأرشفة بنجاح" : "اكتملت مع وجود أخطاء"}
            </div>
            <div className="mt-1">رُفعت {result.uploaded} مواضيع · تُخطّيت {result.skipped} فارغة · {result.summary.totalComments} تعليق</div>
            {result.errors.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-destructive">{result.errors.length} خطأ</summary>
                <ul className="mt-1 list-disc list-inside">
                  {result.errors.slice(0, 20).map((e, i) => <li key={i} className="font-mono break-all">{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </section>

      <section className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h2 className="text-base sm:text-lg font-extrabold inline-flex items-center gap-2 mb-3">
          <MessageCircle className="h-5 w-5 text-primary" /> تنظيف Firestore بعد الأرشفة
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          بعد أرشفة ناجحة، يمكنك حذف التعليقات القديمة من Firestore لتخفيف الاستهلاك.
          الحذف يتم <strong>فقط</strong> للتعليقات الموجودة في النسخة الأخيرة على GitHub.
        </p>
        <div className="mt-4 grid grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-bold">حذف التعليقات الأقدم من (يوم)</label>
            <input
              type="number"
              min={7}
              value={purgeDays}
              onChange={(e) => setPurgeDays(Math.max(7, parseInt(e.target.value || "180", 10)))}
              className="w-full mt-1.5 px-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            type="button"
            onClick={purgeOld}
            disabled={purgeBusy || !result}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive hover:text-destructive-foreground text-sm disabled:opacity-50"
          >
            {purgeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            حذف
          </button>
        </div>
        {!result && (
          <p className="text-[11px] text-muted-foreground mt-2">
            شغّل «أرشفة الآن» أوّلًا — الزرّ سيُفعَّل بعد نجاح الأرشفة.
          </p>
        )}
        {purgeMsg && (
          <div className="mt-3 text-sm bg-background border border-border rounded-lg p-3">{purgeMsg}</div>
        )}
      </section>
    </div>
  );
}

/* =================== Ads tab =================== */

function AdsTab() {
  // استخدام lazy import لتجنّب أي مشاكل SSR
  
  const [cfg, setCfg] = useAdConfig();

  const kinds: { key: "movie" | "series" | "trailer"; label: string }[] = [
    { key: "movie",   label: "أفلام" },
    { key: "series",  label: "مسلسلات" },
    { key: "trailer", label: "ترايلرات" },
  ];

  function update<K extends keyof typeof DEFAULT_AD_CONFIG.movie>(
    k: "movie" | "series" | "trailer",
    field: K,
    value: (typeof DEFAULT_AD_CONFIG.movie)[K],
  ) {
    setCfg({ ...cfg, [k]: { ...cfg[k], [field]: value } });
  }

  function togglePlacement(k: "movie" | "series" | "trailer", p: "pre" | "mid" | "post") {
    const cur = cfg[k].placements;
    const next = cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
    update(k, "placements", next);
  }

  return (
    <div className="space-y-5">
      <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="grid place-items-center w-10 h-10 rounded-xl bg-primary/15 text-primary">
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-extrabold">توزيع الإعلانات حسب نوع المحتوى</h2>
            <p className="text-xs text-muted-foreground">يطبَّق على مشغّل Rawan UI مباشرة بعد الحفظ.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {kinds.map(({ key, label }) => {
            const rule = cfg[key];
            return (
              <div key={key} className="border border-border rounded-xl p-3 bg-background">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-extrabold">{label}</h3>
                  <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={(e) => update(key, "enabled", e.target.checked)}
                    /> مفعّل
                  </label>
                </div>

                <div className="text-xs font-bold mt-2 mb-1">أماكن الظهور</div>
                <div className="flex gap-1.5 mb-3">
                  {(["pre", "mid", "post"] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => togglePlacement(key, p)}
                      className={`px-2 py-1 rounded text-xs font-bold border transition ${
                        rule.placements.includes(p)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card border-border text-muted-foreground"
                      }`}
                    >
                      {p === "pre" ? "قبل" : p === "mid" ? "وسط" : "نهاية"}
                    </button>
                  ))}
                </div>

                <label className="block text-xs font-bold">عدد فواصل الوسط</label>
                <input
                  type="number" min={0} max={10}
                  value={rule.midCount}
                  onChange={(e) => update(key, "midCount", Math.max(0, Number(e.target.value) || 0))}
                  className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-sm"
                />

                <label className="block text-xs font-bold mt-2">مدة الإعلان (ث)</label>
                <input
                  type="number" min={3} max={60}
                  value={rule.durationSec}
                  onChange={(e) => update(key, "durationSec", Math.max(3, Number(e.target.value) || 3))}
                  className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-sm"
                />

                <label className="block text-xs font-bold mt-2">الفترة بين الفواصل (دقيقة)</label>
                <input
                  type="number" min={0} max={120}
                  value={rule.intervalMin}
                  onChange={(e) => update(key, "intervalMin", Math.max(0, Number(e.target.value) || 0))}
                  className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-sm"
                />
                <p className="text-[10px] text-muted-foreground mt-1">0 = توزيع متساوٍ حسب العدد.</p>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setCfg(DEFAULT_AD_CONFIG)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs"
          >
            استعادة الافتراضي
          </button>
          <PublishAdConfigButton cfg={cfg} />
        </div>
      </section>
    </div>
  );
}

function PublishAdConfigButton({ cfg }: { cfg: typeof DEFAULT_AD_CONFIG }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  async function publish() {
    setBusy(true); setStatus(null);
    const res = await uploadJsonToGithub({
      relativePath: "ad-config.json",
      jsonContent: JSON.stringify(cfg, null, 2),
      message: "chore(content): publish player ad config",
    });
    setBusy(false);
    setStatus(res.ok ? "تمت إضافة إعدادات الإعلانات إلى المسودّات ✓" : `فشل الحفظ: ${res.error}`);
    setTimeout(() => setStatus(null), 5000);
  }
  return (
    <>
      <button onClick={publish} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        حفظ إعدادات الإعلانات في المسودّات
      </button>
      {status && <span className="text-xs text-muted-foreground">{status}</span>}
    </>
  );
}

/* LogsTab moved to @/components/rawan/LogsTab */

/* =================== Site Ads tab (AdSense + Adsterra) =================== */

function SiteAdsTab() {
  const [cfg, setCfg] = useSiteAds();
  const [openSlot, setOpenSlot] = useState<AdSlotKey | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushStatus, setPushStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  function patchSlot(key: AdSlotKey, patch: Partial<SlotConfig>) {
    const existing = cfg.slots[key] ?? { enabled: false, network: "adsense" as const };
    setCfg({ ...cfg, slots: { ...cfg.slots, [key]: { ...existing, ...patch } } });
  }

  async function publishAdsToGithub() {
    setPushing(true);
    setPushStatus(null);
    try {
      // 1) site-ads.json — every visitor fetches this and renders the configured slots.
      const r1 = await uploadTextToGithub({
        absolutePath: SITE_ADS_REMOTE_REPO_PATH,
        textContent: JSON.stringify(cfg, null, 2),
        message: "chore(ads): publish site ads config",
      });
      if (!r1.ok) throw new Error(r1.error);

      // 2) ads.txt — only when AdSense gave us content for it.
      if (cfg.adsense.adsTxt && cfg.adsense.adsTxt.trim()) {
        const r2 = await uploadTextToGithub({
          absolutePath: "public/ads.txt",
          textContent: cfg.adsense.adsTxt.trim() + "\n",
          message: "chore(ads): update ads.txt",
        });
        if (!r2.ok) throw new Error(r2.error);
      }
      setPushStatus({ ok: true, msg: "تمت إضافة إعدادات الإعلانات إلى المسودّات ✓ — انشر الكل من تبويب «المسودّات»" });
    } catch (e) {
      setPushStatus({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setPushing(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Master + Network configuration */}
      <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="grid place-items-center w-10 h-10 rounded-xl bg-primary/15 text-primary shrink-0">
            <Megaphone className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold">إعلانات الموقع</h2>
            <p className="text-xs text-muted-foreground">إدارة AdSense و Adsterra وتوزيع المواضع في الصفحات.</p>
          </div>
          <label className="ms-auto inline-flex items-center gap-2 text-sm cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={cfg.masterEnabled}
              onChange={(e) => setCfg({ ...cfg, masterEnabled: e.target.checked })}
            />
            <span className="font-bold">المفتاح الرئيسي</span>
          </label>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* AdSense */}
          <div className="border border-border rounded-xl p-3 bg-background">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold">Google AdSense</h3>
              <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={cfg.adsense.enabled}
                  onChange={(e) => setCfg({ ...cfg, adsense: { ...cfg.adsense, enabled: e.target.checked } })}
                /> مفعّل
              </label>
            </div>
            <label className="block text-xs font-bold">Publisher ID</label>
            <input
              type="text"
              dir="ltr"
              placeholder="ca-pub-XXXXXXXXXXXXXXXX"
              value={cfg.adsense.publisherId}
              onChange={(e) => setCfg({ ...cfg, adsense: { ...cfg.adsense, publisherId: e.target.value.trim() } })}
              className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-sm font-mono"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              يُحقن سكربت AdSense تلقائياً في &lt;head&gt; باستخدام هذا المعرّف، وهو ما تحتاجه Google للتحقّق من الملكية وعرض الإعلانات.
            </p>
            <label className="block text-xs font-bold mt-3">محتوى ads.txt (اختياري)</label>
            <textarea
              dir="ltr"
              rows={3}
              placeholder="google.com, pub-XXXX, DIRECT, f08c47fec0942fa0"
              value={cfg.adsense.adsTxt || ""}
              onChange={(e) => setCfg({ ...cfg, adsense: { ...cfg.adsense, adsTxt: e.target.value } })}
              className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-xs font-mono"
            />
          </div>

          {/* Adsterra */}
          <div className="border border-border rounded-xl p-3 bg-background">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold">Adsterra</h3>
              <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={cfg.adsterra.enabled}
                  onChange={(e) => setCfg({ ...cfg, adsterra: { ...cfg.adsterra, enabled: e.target.checked } })}
                /> مفعّل
              </label>
            </div>
            <label className="block text-xs font-bold">كود التحقّق (meta tag)</label>
            <textarea
              dir="ltr"
              rows={2}
              placeholder='<meta name="adsterra-verification" content="...">'
              value={cfg.adsterra.verificationMeta || ""}
              onChange={(e) => setCfg({ ...cfg, adsterra: { ...cfg.adsterra, verificationMeta: e.target.value } })}
              className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-xs font-mono"
            />
            <label className="block text-xs font-bold mt-3">سكربت عام (اختياري)</label>
            <textarea
              dir="ltr"
              rows={3}
              placeholder="<script>...</script>"
              value={cfg.adsterra.globalScript || ""}
              onChange={(e) => setCfg({ ...cfg, adsterra: { ...cfg.adsterra, globalScript: e.target.value } })}
              className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-xs font-mono"
            />
            <p className="text-[11px] text-muted-foreground mt-1">يُحقن في &lt;head&gt; ليسمح للشبكة بعرض إعلانات Popunder/Banner.</p>
          </div>
        </div>
      </section>

      {/* Slot manager */}
      <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <h2 className="text-lg font-extrabold mb-1">المواضع داخل الصفحات</h2>
        <p className="text-xs text-muted-foreground mb-4">
          فعّل أي موضع، اختر الشبكة، والصق كود الإعلان. التوزيع الافتراضي مُحسَّن للسيو (إعلان واحد علوي، واحد وسط، واحد سفلي).
        </p>

        <div className="grid gap-2">
          {ALL_SLOT_KEYS.map((key) => {
            const slot = cfg.slots[key] ?? { enabled: false, network: "adsense" as const, label: key };
            const isOpen = openSlot === key;
            return (
              <div key={key} className="border border-border rounded-xl bg-background overflow-hidden">
                <div className="flex items-center gap-2 p-3">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer min-w-0 flex-1">
                    <input
                      type="checkbox"
                      checked={slot.enabled}
                      onChange={(e) => patchSlot(key, { enabled: e.target.checked })}
                    />
                    <span className="font-bold truncate">{slot.label || key}</span>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">{key}</span>
                  </label>
                  <select
                    value={slot.network}
                    onChange={(e) => patchSlot(key, { network: e.target.value as "adsense" | "adsterra" })}
                    className="px-2 py-1 rounded bg-card border border-border text-xs"
                  >
                    <option value="adsense">AdSense</option>
                    <option value="adsterra">Adsterra</option>
                  </select>
                  <button
                    onClick={() => setOpenSlot(isOpen ? null : key)}
                    className="px-2 py-1 rounded text-xs bg-card border border-border hover:bg-accent"
                  >
                    {isOpen ? "إخفاء" : "تفاصيل"}
                  </button>
                </div>
                {isOpen && (
                  <div className="px-3 pb-3 border-t border-border bg-card/40">
                    {slot.network === "adsense" ? (
                      <>
                        <label className="block text-xs font-bold mt-2">Ad Slot ID</label>
                        <input
                          type="text"
                          dir="ltr"
                          placeholder="1234567890"
                          value={slot.adsenseSlotId || ""}
                          onChange={(e) => patchSlot(key, { adsenseSlotId: e.target.value.trim() })}
                          className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-sm font-mono"
                        />
                      </>
                    ) : (
                      <>
                        <label className="block text-xs font-bold mt-2">كود Adsterra</label>
                        <textarea
                          dir="ltr"
                          rows={4}
                          placeholder="<script ...></script>"
                          value={slot.adsterraCode || ""}
                          onChange={(e) => patchSlot(key, { adsterraCode: e.target.value })}
                          className="w-full mt-1 px-2 py-1.5 rounded bg-card border border-border text-xs font-mono"
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={publishAdsToGithub}
            disabled={pushing}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs font-bold disabled:opacity-60"
          >
            {pushing ? "جارٍ الحفظ…" : "حفظ إعدادات الإعلانات في المسودّات"}
          </button>
          <button
            onClick={() => setCfg(DEFAULT_SITE_ADS)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs"
          >
            استعادة الافتراضي
          </button>
          <span className="text-[11px] text-muted-foreground">
            التعديلات هنا تُحفَظ كمسوّدة. للنشر الفعلي للزوار اذهب إلى تبويب «المسودّات» واضغط «نشر الكل».
          </span>
          {pushStatus && (
            <span className={`text-[11px] font-bold ${pushStatus.ok ? "text-emerald-500" : "text-destructive"}`}>
              {pushStatus.msg}
            </span>
          )}
        </div>

      </section>
    </div>
  );
}

/* =================== Social links tab =================== */

function SocialTab() {
  const [links, setLinks] = useSocialLinks();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const fields: { key: SocialKey; label: string; placeholder: string }[] = [
    { key: "facebook",  label: "فيسبوك",       placeholder: "https://facebook.com/yourpage" },
    { key: "instagram", label: "إنستغرام",     placeholder: "https://instagram.com/your" },
    { key: "x",         label: "X (تويتر)",    placeholder: "https://x.com/your" },
    { key: "telegram",  label: "تيليجرام",     placeholder: "https://t.me/your" },
    { key: "whatsapp",  label: "واتساب",       placeholder: "https://wa.me/212XXXXXXXXX" },
    { key: "youtube",   label: "يوتيوب",       placeholder: "https://youtube.com/@your" },
    { key: "github",    label: "GitHub",       placeholder: "https://github.com/your" },
  ];

  async function publish() {
    setBusy(true); setStatus(null);
    const res = await uploadJsonToGithub({
      relativePath: SOCIAL_REMOTE_PATH,
      jsonContent: JSON.stringify({ ...DEFAULT_SOCIAL_LINKS, ...links }, null, 2),
      message: "chore(content): publish social links",
    });
    setBusy(false);
    setStatus(res.ok ? "تمت إضافة روابط السوشيال إلى المسودّات ✓" : `فشل الحفظ: ${res.error}`);
    setTimeout(() => setStatus(null), 5000);
  }

  return (
    <section className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="grid place-items-center w-10 h-10 rounded-xl bg-primary/15 text-primary">
          <Share2 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-extrabold">روابط السوشيال ميديا</h2>
          <p className="text-xs text-muted-foreground">تظهر في الفوتر وعلى بطاقة «تابعنا» داخل صفحات المقالات.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {fields.map((f) => (
          <Field key={f.key} label={f.label}
            value={links[f.key] ?? ""}
            onChange={(v) => setLinks({ [f.key]: v })}
            placeholder={f.placeholder} />
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={() => setLinks({ ...DEFAULT_SOCIAL_LINKS })}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-card border border-border hover:bg-accent text-xs">
          استعادة الافتراضي
        </button>
        <button onClick={publish} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          حفظ في المسودّات
        </button>
        {status && <span className="text-xs text-muted-foreground">{status}</span>}
      </div>
    </section>
  );
}

